import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useWebSocket } from '../WebSocketContext';
import {
  createProjectWorkflowFlow,
  fetchProjectWorkflowFlows,
  updateProjectWorkflowFlowAutomation,
  updateProjectWorkflowFlowLayout
} from '../../Utils/actionsApi';
import SettingsDialog from './SettingsDialog';
import AddActionMenu from '../AddActionMenu';
import WorkflowActionNode from '../WorkflowActionNode';
import WorkflowFlowHeader from '../WorkflowFlowHeader';
import WorkflowFlowAutomationDialog from '../WorkflowFlowAutomationDialog';
import WorkflowConfirmDialogs from '../WorkflowConfirmDialogs';
import WorkflowFlowManageDialogs from '../WorkflowFlowManageDialogs';
import { isActionEnabled } from '../../Utils/actionSettingsUtils';
import { fetchTimeeventCount } from '../../Utils/timeEventsApi';
import { getTriggerVisualConfig } from '../../Utils/workflowTriggerVisuals';

// Import icons needed for displaying existing settings
import icon_createfile from '../../images/flow_createfile.png';
import icon_copyfile from '../../images/flow_copyfile.png';
import icon_createfolder from '../../images/folder.png';
import icon_createdocx from '../../images/docx.png';
import icon_createexcel from '../../images/excelexport.png';
import icon_createpdf from '../../images/flow_createpdf.png'; // Reusing doc icon for now
import icon_sendemail from '../../images/flow_sendemail.png';
import icon_callmcp from '../../images/flow_callmcp.png';
import icon_sendapi from '../../images/flow_sendapi.png';
import icon_sendmsgteams from '../../images/flow_teamssendmsg.png';
import icon_createtagbrowseweb from '../../images/flow_createtagbrowseweb.png';
import icon_cmqverifydoctor from '../../images/flow_verifydoctor.png'; // Reusing the same icon for now
import icon_oiiqverifynurse from '../../images/flow_verifynurse.png'; // Reusing the same icon for now
import icon_validatelicense from '../../images/validateform.png'; // Icon for rule-based license validation
import icon_cropimage from '../../images/flow_createfile.png'; // Using createfile icon temporarily for crop image
import icon_genai from '../../images/flow_genai.png';
import icon_javascript from '../../images/flow_callmcp.png'; // Using callmcp icon temporarily for javascript
import { AppTooltip } from '../Common';


// Constants for better maintainability
const BUILTIN_SUPPORTED_SETTING_TYPES = new Set([
  'createfile',
  'addcsvcolumn',
  'addexcelcolumn',
  'addexcelrow',
  'copyfile',
  'createfolder',
  'createproject',
  'generatewidget',
  'cropimage',
  'createdocx',
  'createdocxtemplate',
  'createdocxtemplatev2',
  'replacewordtext',
  'anonymizepii',
  'createexcelfromtemplate',
  'addpassworddocx',
  'addpasswordexcel',
  'createpdf',
  'insertimagetopdf',
  'searchandinsertimagetopdf',
  'searchandinsertrichtexttopdf',
  'createzipfile',
  'createjsonfile',
  'createxmlfile',
  'sendapi',
  'sendOutlookEmail',
  'moveOutlookEmail',
  'sendGmailEmail',
  'sendLinkedinPost',
  'sendLinkedinCompanyPost',
  'msteamsSendMessage',
  'callmcp',
  'createtagbrowseweb',
  'browseweb',
  'computeruse',
  'cmqverifydoctor',
  'oiiqverifynurse',
  'generativeanswer',
  'generativeanswerfile',
  'deterministicanswer',
  'formextraction',
  'extractimagetotag',
  'saveimagetofile',
  'validatereservation',
  'validatedoctorlicense',
  'simplejavascript',
  'manualtag',
  'triggerflow',
  'looptriggerflow',
  'convertdocxtopdf',
  'convertpdftodocx',
  'documentlegalpdftodocx',
  'convertpdftohtml',
  'convertpdfimagetopdftext',
  'rotatepdfimageocrpdf',
  'pdftotoword',
  'dashboardsnapshot',
  'weatherapi'
]);

const ICON_MAP = {
  sendapi: icon_sendapi,
  msteamsSendMessage: icon_sendmsgteams,
  callmcp: icon_callmcp,
  browseweb: icon_createtagbrowseweb,
  computeruse: icon_createtagbrowseweb,
  cmqverifydoctor: icon_cmqverifydoctor,
  oiiqverifynurse: icon_oiiqverifynurse,
  createfile: icon_createfile,
  addcsvcolumn: icon_createfile,
  addexcelcolumn: icon_createexcel,
  addexcelrow: icon_createexcel,
  copyfile: icon_copyfile, // Using copyfile icon for copy
  createfolder: icon_createfolder, // Using folder icon for folder creation
  createproject: icon_createfolder, // Using folder icon for project creation
  generatewidget: icon_genai, // Using GenAI icon for widget generation
  cropimage: icon_cropimage, // Using cropimage icon for image cropping
  generativeanswer: icon_genai, // Using GenAI icon for AI generation
  generativeanswerfile: icon_genai, // Using GenAI icon for AI generation
  deterministicanswer: icon_genai, // Using GenAI icon for prompt-driven deterministic planning
  formextraction: icon_genai,
  extractimagetotag: icon_genai,
  saveimagetofile: icon_cropimage,
  validatereservation: icon_cmqverifydoctor, // Using verification icon for reservation validation
  validatedoctorlicense: icon_cmqverifydoctor, // Using verification icon for doctor license validation
  createdocx: icon_createdocx,
  manualtag: icon_genai,
  triggerflow: icon_callmcp,
  looptriggerflow: icon_callmcp,
  dashboardsnapshot: icon_genai,
  createdocxtemplate: icon_createdocx, // Using same icon as createdocx
  createdocxtemplatev2: icon_createdocx,
  replacewordtext: icon_createdocx,
  anonymizepii: icon_createdocx,
  createexcelfromtemplate: icon_createexcel,
  addpassworddocx: icon_createdocx, // Using DOCX icon with lock overlay
  addpasswordexcel: icon_createexcel,
  createpdf: icon_createpdf, // Using createpdf icon for PDF
  insertimagetopdf: icon_createpdf, // Using createpdf icon for PDF insertion
  searchandinsertimagetopdf: icon_createpdf, // Using createpdf icon for AI-assisted PDF insertion
  searchandinsertrichtexttopdf: icon_createpdf, // Using createpdf icon for AI-assisted rich text insertion
  createzipfile: icon_createfile,
  createjsonfile: icon_createfile,
  createxmlfile: icon_createfile,
  convertdocxtopdf: icon_createpdf, // Using createpdf icon for DOCX to PDF conversion
  convertpdftodocx: icon_createpdf, // Using createpdf icon for PDF to DOCX conversion
  documentlegalpdftodocx: icon_createpdf, // Using createpdf icon for the legal PDF to DOCX pipeline
  convertpdftohtml: icon_createpdf, // Using createpdf icon for PDF/image to HTML conversion
  convertpdfimagetopdftext: icon_createpdf, // Using createpdf icon for PDF/image to text PDF conversion
  rotatepdfimageocrpdf: icon_createpdf, // Using createpdf icon for OCR-based PDF rotation
  pdftotoword: icon_createpdf, // Using createpdf icon for PDF text to Word conversion
  simplejavascript: icon_javascript, // Using callmcp icon for JavaScript
  sendOutlookEmail: icon_sendemail,
  moveOutlookEmail: icon_sendemail,
  sendGmailEmail: icon_sendemail,
  sendLinkedinPost: icon_sendmsgteams,
  sendLinkedinCompanyPost: icon_sendmsgteams,
  weatherapi: icon_sendapi
};

const DEFAULT_ICON = icon_createfile;
const DEFAULT_LOOP_TAG_NAME = 'loopTag';
const TEMPLATE_GUARDED_COMMANDS = new Set([
  'saveActionSettings',
  'updateActionExecution',
  'updateActionTargetExecution',
  'deleteExportSettings',
  'toggleFlowEnable',
  'toggleActionScope',
  'flowRename',
  'swapfloworder',
  'createWorkflowFlow',
  'renameWorkflowFlow',
  'deleteWorkflowFlow',
  'swapWorkflowFlowOrder',
  'moveActionToFlow'
]);

// Helper functions
const filterSupportedSettings = (settings, pluginTypeSet = new Set()) =>
  settings.filter(
    (setting) =>
      BUILTIN_SUPPORTED_SETTING_TYPES.has(setting.type) ||
      setting?.isPlugin === true ||
      pluginTypeSet.has(setting.type)
  );

const getSettingIcon = (type) => ICON_MAP[type] || DEFAULT_ICON;

const getNameMap = (t) => ({
  createfile: t("createfile"),
  addcsvcolumn: t("addcsvcolumn", "Add CSV Column"),
  addexcelcolumn: t("addexcelcolumn", "Add Excel Column"),
  addexcelrow: t("addexcelrow", "Add Excel Row"),
  copyfile: t("copyfile"),
  createfolder: t("createfolder", "Create Folder"),
  createproject: t("createproject", "Create Project"),
  generatewidget: t("widget.update_widget", "Update Widget"),
  cropimage: t("cropimage", "Crop Image"),
  createdocx: t("createdocx"),
  createdocxtemplate: t("workflow.createdocxtemplate", "Create DOCX Template"),
  createdocxtemplatev2: t("workflow.create_docx_template_v2", "Create DOCX Template V2"),
  replacewordtext: t("workflow.replace_word_text", "Replace Word Text"),
  anonymizepii: t("workflow.anonymize_pii", "Anonymize PII"),
  createexcelfromtemplate: t("workflow.create_excel_template", "Create Excel from Template"),
  addpassworddocx: t("workflow.addpassworddocx", "Add Password to DOCX"),
  addpasswordexcel: t("workflow.addpasswordexcel", "Add Password to Excel"),
  createpdf: t("createpdf"),
  insertimagetopdf: t("workflow.insert_image_into_pdf.title", "Insert Image Into PDF"),
  searchandinsertimagetopdf: t("workflow.search_and_insert_image_into_pdf.title", "Search Zone and Insert Image Into PDF"),
  searchandinsertrichtexttopdf: t("workflow.search_and_insert_rich_text_into_pdf.title", "Search Zone and Insert Rich Text Into PDF"),
  createzipfile: t("createzipfile", "Create ZIP Archive"),
  createjsonfile: t("createjsonfile", "Create JSON File"),
  createxmlfile: t("createxmlfile", "Create XML File"),
  sendapi: t("sendapi"),
  sendOutlookEmail: t("workflow.send_outlook_email.title", "Send email (Outlook)"),
  moveOutlookEmail: t("workflow.move_outlook_email.title", "Move email (Outlook)"),
  sendGmailEmail: t("workflow.send_gmail_email.title", "Send email (Gmail)"),
  sendLinkedinPost: t("workflow.send_linkedin_post.title", "Publish post (LinkedIn)"),
  sendLinkedinCompanyPost: t("workflow.send_linkedin_company_post.title", "Publish post (LinkedIn Company)"),
  msteamsSendMessage: t("workflow.send_teams_message", "Send Teams Message"),
  callmcp: t("callmcp"),
  browseweb: t("workflow.browseweb"),
  computeruse: t("workflow.computeruse", "Application Use"),
  cmqverifydoctor: t("cmqverifydoctor", "CMQ Verify Doctor"),
  oiiqverifynurse: t("oiiqverifynurse", "OIIQ Verify Nurse"),
  generativeanswer: t("workflow.generativeanswer"),
  generativeanswerfile: t("workflow.generativeanswerfile", "File Generative Answer"),
  deterministicanswer: t("deterministicanswer", "Deterministic Answer"),
  formextraction: t("workflow.formextraction", "Form Extraction"),
  extractimagetotag: t("workflow.extractimagetotag", "Extract Image To Tag"),
  saveimagetofile: t("saveimagetofile", "Save Image Tag To File"),
  validatereservation: t("validatereservation", "Validate Reservation"),
  validatedoctorlicense: t("validatedoctorlicense", "Validate Doctor License"),
  simplejavascript: t("simplejavascript", "Simple JavaScript"),
  manualtag: t("manualtag", "Manual Tag"),
  triggerflow: t("workflow.triggerflow", "Trigger Flow"),
  looptriggerflow: t("workflow.looptriggerflow", "Find Items and Repeat Flow"),
  dashboardsnapshot: t("manualSnap.title", "Dashboard Snapshot"),
  convertdocxtopdf: t("workflow.convertdocxtopdf", "Convert DOCX to PDF"),
  convertpdftodocx: t("workflow.convertpdftodocx", "Convert PDF to DOCX"),
  documentlegalpdftodocx: t("workflow.documentlegalpdftodocx", "Document Legal - PDF vers DOCX"),
  convertpdftohtml: t("workflow.convertpdftohtml", "Convert PDF/Image to HTML"),
  convertpdfimagetopdftext: t("workflow.convertpdfimagetopdftext", "Convert PDF/Image to Text PDF"),
  rotatepdfimageocrpdf: t("workflow.rotatepdfimageocrpdf", "Straighten the Page"),
  pdftotoword: t("workflow.pdf_text_to_word", "PDF Text to Word"),
  weatherapi: t("weatherapi", "Weather API")
});

const isTemporaryPlaceholderName = (value) => typeof value === 'string' && value.trim().toLowerCase().startsWith('temp_');

const getSettingName = (setting, nameMap, pluginNameByType) => {
  const displayName = String(setting?.name || '').trim();
  if (displayName && !isTemporaryPlaceholderName(displayName)) {
    return displayName;
  }
  return pluginNameByType[setting.type] || nameMap[setting.type] || "Workflow";
};

const parsePositiveInteger = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const isTemporarySettingId = (value) => typeof value === 'string' && value.startsWith('temp_');

const getLoopTriggerTargetFlowId = (setting) => {
  if (setting?.type !== 'looptriggerflow') {
    return null;
  }

  const customData = setting?.customData || setting?.data || {};
  return parsePositiveInteger(customData?.targetFlowId ?? customData?.target_flow_id);
};

const getLoopTriggerTagName = (setting) => {
  if (setting?.type !== 'looptriggerflow') {
    return null;
  }

  const customData = setting?.customData || setting?.data || {};
  const rawValue = customData?.loopTagName ?? customData?.loop_tag_name ?? DEFAULT_LOOP_TAG_NAME;
  const normalizedValue = String(rawValue || '').trim();
  return normalizedValue || DEFAULT_LOOP_TAG_NAME;
};

const normalizeActionExecutionMode = (value) => (
  String(value || 'sequential').trim().toLowerCase() === 'parallel' ? 'parallel' : 'sequential'
);

const normalizeActionExecutionGroup = (value, mode) => {
  const normalized = String(value || '').trim();
  if (!normalized || normalizeActionExecutionMode(mode) !== 'parallel') {
    return null;
  }
  return normalized;
};

const normalizeActionTargetExecutionMode = (value) => (
  String(value || 'sequential').trim().toLowerCase() === 'parallel' ? 'parallel' : 'sequential'
);

const normalizeMaxParallelTargets = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) {
    return 5;
  }
  return Math.min(Math.max(parsed, 1), 25);
};

const buildActionExecutionStages = (actions = []) => {
  const stages = [];
  actions.forEach((action) => {
    const mode = normalizeActionExecutionMode(action.execution_mode);
    const group = normalizeActionExecutionGroup(action.execution_group, mode);
    if (mode === 'parallel' && group) {
      const previousStage = stages[stages.length - 1];
      if (
        previousStage
        && previousStage.execution_mode === 'parallel'
        && previousStage.execution_group === group
      ) {
        previousStage.actions.push(action);
      } else {
        stages.push({ execution_mode: 'parallel', execution_group: group, actions: [action] });
      }
      return;
    }
    stages.push({ execution_mode: 'sequential', execution_group: null, actions: [action] });
  });
  return stages;
};

const hasTagDefinition = (tags, tagName) => {
  const normalizedTagName = String(tagName || '').trim().toLowerCase();
  if (!normalizedTagName || !Array.isArray(tags)) {
    return false;
  }

  return tags.some((tag) => {
    const candidate = tag?.variable ?? tag?.value ?? tag;
    return String(candidate || '').trim().toLowerCase() === normalizedTagName;
  });
};

const getActionSearchText = (setting) => {
  if (!setting) {
    return '';
  }
  const customData = setting.customData || setting.data || {};
  let customDataText = '';
  try {
    customDataText = JSON.stringify(customData);
  } catch (error) {
    customDataText = '';
  }
  return [
    setting.id,
    setting.name,
    setting.type,
    setting.scope,
    customData?.tag,
    customData?.name,
    customData?.description,
    customData?.variable,
    customData?.target_tag,
    customData?.targetTag,
    customDataText
  ]
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value).toLowerCase())
    .join(' ');
};

const normalizeFlowAutomationUnit = (rawUnit) => {
  const normalized = String(rawUnit || '').trim().toLowerCase();
  if (['minute', 'minutes', 'min', 'mins', 'm'].includes(normalized)) {
    return 'minutes';
  }
  if (['hour', 'hours', 'hr', 'hrs', 'h', 'heure', 'heures'].includes(normalized)) {
    return 'hours';
  }
  if (['day', 'days', 'd', 'jour', 'jours'].includes(normalized)) {
    return 'days';
  }
  return 'minutes';
};

const WEEKDAY_REFERENCE_DATE_UTC = Date.UTC(2024, 0, 1);
const WEEKDAY_LABEL_FALLBACKS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_error) {
    return 'UTC';
  }
};

const normalizeFlowScheduleNumberList = (rawValue, minimum, maximum) => {
  if (rawValue === null || rawValue === undefined) {
    return [];
  }

  let values = rawValue;
  if (Array.isArray(rawValue)) {
    values = rawValue;
  } else if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return [];
    }
    try {
      const parsedJson = JSON.parse(trimmed);
      values = Array.isArray(parsedJson) ? parsedJson : [parsedJson];
    } catch (_error) {
      values = trimmed.split(/[,\s;]+/);
    }
  } else {
    values = [rawValue];
  }

  const normalized = [];
  values.forEach((value) => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < minimum || parsed > maximum) {
      return;
    }
    if (!normalized.includes(parsed)) {
      normalized.push(parsed);
    }
  });
  normalized.sort((a, b) => a - b);
  return normalized;
};

const normalizeFlowScheduleDayOfWeek = (rawValue) => {
  const aliases = {
    monday: 0,
    mon: 0,
    tuesday: 1,
    tue: 1,
    tues: 1,
    wednesday: 2,
    wed: 2,
    thursday: 3,
    thu: 3,
    thur: 3,
    thurs: 3,
    friday: 4,
    fri: 4,
    saturday: 5,
    sat: 5,
    sunday: 6,
    sun: 6
  };

  const values = Array.isArray(rawValue) ? rawValue : normalizeFlowScheduleNumberList(rawValue, 0, 7);
  const normalized = [];
  values.forEach((candidate) => {
    const stringCandidate = String(candidate ?? '').trim().toLowerCase();
    if (!stringCandidate) {
      return;
    }
    const numericCandidate = Number(stringCandidate);
    if (Number.isInteger(numericCandidate)) {
      if (numericCandidate >= 0 && numericCandidate <= 6 && !normalized.includes(numericCandidate)) {
        normalized.push(numericCandidate);
        return;
      }
      if (numericCandidate >= 1 && numericCandidate <= 7) {
        const coerced = numericCandidate - 1;
        if (!normalized.includes(coerced)) {
          normalized.push(coerced);
        }
        return;
      }
    }
    const mapped = aliases[stringCandidate];
    if (mapped !== undefined && !normalized.includes(mapped)) {
      normalized.push(mapped);
    }
  });
  normalized.sort((a, b) => a - b);
  return normalized;
};

const normalizeFlowScheduleHour = (rawValue) => normalizeFlowScheduleNumberList(rawValue, 0, 23);

const normalizeFlowScheduleMinute = (rawValue) => normalizeFlowScheduleNumberList(rawValue, 0, 59);

const normalizeFlowScheduleDayOfMonth = (rawValue) => normalizeFlowScheduleNumberList(rawValue, 1, 31);

const normalizeFlowScheduleMonth = (rawValue) => normalizeFlowScheduleNumberList(rawValue, 1, 12);

const normalizeFlowScheduleTimezone = (rawValue) => {
  const normalized = String(rawValue || '').trim();
  return normalized || getBrowserTimezone();
};

const formatFlowWeekdayLabel = (weekdayIndex, language) => {
  const normalizedIndex = normalizeFlowScheduleDayOfWeek([weekdayIndex])[0] ?? 0;
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      weekday: 'long',
      timeZone: 'UTC'
    }).format(new Date(WEEKDAY_REFERENCE_DATE_UTC + (normalizedIndex * 24 * 60 * 60 * 1000)));
  } catch (_error) {
    return WEEKDAY_LABEL_FALLBACKS[normalizedIndex] || WEEKDAY_LABEL_FALLBACKS[0];
  }
};

const MONTH_REFERENCE_DATE_UTC = Date.UTC(2024, 0, 1);
const MONTH_LABEL_FALLBACKS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const formatFlowMonthLabel = (monthIndex, language) => {
  const normalizedIndex = normalizeFlowScheduleMonth([monthIndex])[0] ?? 1;
  try {
    return new Intl.DateTimeFormat(language || 'en', {
      month: 'short',
      timeZone: 'UTC'
    }).format(new Date(MONTH_REFERENCE_DATE_UTC + ((normalizedIndex - 1) * 31 * 24 * 60 * 60 * 1000)));
  } catch (_error) {
    return MONTH_LABEL_FALLBACKS[normalizedIndex - 1] || MONTH_LABEL_FALLBACKS[0];
  }
};

const createFlowScheduleLabelMap = (options) => new Map((options || []).map((option) => [Number(option.value), option.label]));

const formatFlowScheduleSelectionList = (values, labelMap) => {
  const normalizedValues = Array.isArray(values) ? values : [];
  if (!normalizedValues.length) {
    return '';
  }

  return normalizedValues
    .map((value) => labelMap.get(Number(value)) ?? String(value))
    .filter(Boolean)
    .join(', ');
};

const normalizeFlowTriggerMode = (rawMode, fallbackAutoRunEnabled = null) => {
  const normalized = String(rawMode || '').trim().toLowerCase();
  if (['time', 'schedule', 'scheduled', 'auto'].includes(normalized)) {
    return 'time';
  }
  if (['calendar', 'cron', 'weekly', 'week', 'weekly schedule', 'weekly_schedule', 'weekly-schedule'].includes(normalized)) {
    return 'calendar';
  }
  if (['event', 'events', 'on_event', 'on event', 'change'].includes(normalized)) {
    return 'event';
  }
  if (['event_change', 'event change', 'event-change', 'timeevent_change', 'timeevent change', 'timeevent.changed'].includes(normalized)) {
    return 'event_change';
  }
  if (['event_confirmed', 'event confirmed', 'event-confirmed', 'confirmation', 'confirmed', 'on_confirm', 'on confirmation'].includes(normalized)) {
    return 'event_confirmed';
  }
  if (['disabled', 'disable', 'off', 'manual', 'none'].includes(normalized)) {
    return 'disabled';
  }
  return Number(fallbackAutoRunEnabled || 0) === 1 ? 'time' : 'event';
};

const DEFAULT_FLOW_TRIGGER_FILTER = {
  change_type: 'any',
  operation: 'any',
  tag_variable: '',
  value_type: ''
};

const normalizeFlowTriggerFilter = (rawFilter) => {
  let source = rawFilter;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch (_error) {
      source = {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    source = {};
  }

  const changeType = String(source.change_type || source.changeType || 'any').trim().toLowerCase() || 'any';
  const operation = String(source.operation || 'any').trim().toLowerCase() || 'any';
  return {
    ...DEFAULT_FLOW_TRIGGER_FILTER,
    change_type: ['confirm', 'tag', 'date', 'ingestion'].includes(changeType) ? changeType : 'any',
    operation: ['created', 'updated', 'deleted', 'processed'].includes(operation) ? operation : 'any',
    tag_variable: String(source.tag_variable || source.tagVariable || source.variable || '').trim(),
    value_type: String(source.value_type || source.valueType || '').trim()
  };
};

const buildFlowTriggerFilterPayload = (rawFilter) => {
  const filter = normalizeFlowTriggerFilter(rawFilter);
  const payload = {};
  if (filter.change_type && filter.change_type !== 'any') {
    payload.change_type = filter.change_type;
  }
  if (filter.operation && filter.operation !== 'any') {
    payload.operation = filter.operation;
  }
  if (filter.change_type === 'tag') {
    if (filter.tag_variable) {
      payload.tag_variable = filter.tag_variable;
    }
    if (filter.value_type) {
      payload.value_type = filter.value_type;
    }
  }
  return payload;
};

const buildFlowAutomationPayload = (
  triggerMode,
  frequencyValue,
  frequencyUnit,
  scheduleDayOfWeek,
  scheduleDayOfMonth,
  scheduleMonth,
  scheduleHour,
  scheduleMinute,
  scheduleTimezone,
  triggerFilter = DEFAULT_FLOW_TRIGGER_FILTER
) => {
  const normalizedTriggerMode = normalizeFlowTriggerMode(triggerMode);
  const isTimeTriggered = normalizedTriggerMode === 'time';
  const isCalendarTriggered = normalizedTriggerMode === 'calendar';
  const isEventChangeTriggered = normalizedTriggerMode === 'event_change';
  return {
    trigger_mode: normalizedTriggerMode,
    auto_run_enabled: (isTimeTriggered || isCalendarTriggered) ? 1 : 0,
    auto_run_frequency_value: isTimeTriggered ? frequencyValue : 0,
    auto_run_frequency_unit: isTimeTriggered ? frequencyUnit : 'minutes',
    auto_run_schedule_day_of_week: isCalendarTriggered ? normalizeFlowScheduleDayOfWeek(scheduleDayOfWeek) : null,
    auto_run_schedule_day_of_month: isCalendarTriggered ? normalizeFlowScheduleDayOfMonth(scheduleDayOfMonth) : null,
    auto_run_schedule_month: isCalendarTriggered ? normalizeFlowScheduleMonth(scheduleMonth) : null,
    auto_run_schedule_hour: isCalendarTriggered ? normalizeFlowScheduleHour(scheduleHour) : null,
    auto_run_schedule_minute: isCalendarTriggered ? normalizeFlowScheduleMinute(scheduleMinute) : null,
    auto_run_schedule_timezone: isCalendarTriggered ? normalizeFlowScheduleTimezone(scheduleTimezone) : null,
    trigger_filter: isEventChangeTriggered ? buildFlowTriggerFilterPayload(triggerFilter) : null
  };
};

const WORKFLOW_NODE_CONTAINER_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  mb: 3,
  position: 'relative',
  minWidth: { xs: '108px', md: '150px' },
  pt: '25px',
};

const WORKFLOW_CANVAS_NODE_WIDTH = 160;
const WORKFLOW_CANVAS_NODE_HEIGHT = 150;
const WORKFLOW_CANVAS_NODE_CENTER_Y = 65;
const WORKFLOW_CANVAS_NODE_ANCHOR_OFFSET = 42;
const WORKFLOW_CANVAS_STAGE_GAP = 240;
const WORKFLOW_CANVAS_PARALLEL_GAP = 172;
const WORKFLOW_CANVAS_START_X = 90;
const WORKFLOW_CANVAS_START_Y = 130;
const WORKFLOW_CANVAS_DEFAULT_VIEWPORT = { x: 48, y: 34, scale: 1 };
const MAX_MANUAL_FLOW_RUN_TIMEEVENTS = 10;
const WORKFLOW_TRIGGER_NODE_ID = '__flow_trigger__';
const WORKFLOW_ADD_NODE_ID = '__flow_add__';
const WORKFLOW_CANVAS_PERSISTED_NODE_ID_PATTERN = /^(?:action:\d+|__flow_trigger__|__flow_add__)$/;

const getPersistableWorkflowCanvasNodePositions = (nodePositions) => Object.fromEntries(
  Object.entries(nodePositions || {}).filter(([nodeId, position]) => (
    WORKFLOW_CANVAS_PERSISTED_NODE_ID_PATTERN.test(nodeId)
    && Number.isFinite(position?.x)
    && Number.isFinite(position?.y)
  ))
);

const getWorkflowCanvasEdgePath = (sourcePosition, targetPosition) => {
  const sourceCenterX = sourcePosition.x + (WORKFLOW_CANVAS_NODE_WIDTH / 2);
  const sourceCenterY = sourcePosition.y + WORKFLOW_CANVAS_NODE_CENTER_Y;
  const targetCenterX = targetPosition.x + (WORKFLOW_CANVAS_NODE_WIDTH / 2);
  const targetCenterY = targetPosition.y + WORKFLOW_CANVAS_NODE_CENTER_Y;
  const deltaX = targetCenterX - sourceCenterX;
  const deltaY = targetCenterY - sourceCenterY;
  const isHorizontal = Math.abs(deltaX) >= Math.abs(deltaY);
  const direction = isHorizontal
    ? (deltaX >= 0 ? 1 : -1)
    : (deltaY >= 0 ? 1 : -1);
  const distance = isHorizontal ? Math.abs(deltaX) : Math.abs(deltaY);
  const controlOffset = Math.max(60, distance / 2);

  if (isHorizontal) {
    const sourceX = sourceCenterX + (WORKFLOW_CANVAS_NODE_ANCHOR_OFFSET * direction);
    const targetX = targetCenterX - (WORKFLOW_CANVAS_NODE_ANCHOR_OFFSET * direction);
    return `M ${sourceX} ${sourceCenterY} C ${sourceX + (controlOffset * direction)} ${sourceCenterY}, ${targetX - (controlOffset * direction)} ${targetCenterY}, ${targetX} ${targetCenterY}`;
  }

  const sourceY = sourceCenterY + (WORKFLOW_CANVAS_NODE_ANCHOR_OFFSET * direction);
  const targetY = targetCenterY - (WORKFLOW_CANVAS_NODE_ANCHOR_OFFSET * direction);
  return `M ${sourceCenterX} ${sourceY} C ${sourceCenterX} ${sourceY + (controlOffset * direction)}, ${targetCenterX} ${targetY - (controlOffset * direction)}, ${targetCenterX} ${targetY}`;
};

const isInteractiveCanvasTarget = (target) => {
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  return Boolean(target.closest('button, input, textarea, select, [role="button"], [role="menuitem"], [data-canvas-interactive="true"], .MuiMenu-root, .MuiPopover-root'));
};

const WorkflowConnector = () => (
  <Box sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: '48px', md: '76px' },
    px: { xs: 0.5, md: 1 },
    pt: '25px',
    height: { xs: '60px', md: '80px' },
    flexShrink: 0,
  }}>
    <Box sx={{
      position: 'relative',
      width: { xs: '38px', md: '64px' },
      height: '26px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        height: '2px',
        borderRadius: '999px',
        background: 'linear-gradient(90deg, rgba(87, 87, 176, 0.12) 0%, rgba(87, 87, 176, 0.54) 50%, rgba(87, 87, 176, 0.12) 100%)',
      },
    }}>
      <Box sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: 22, md: 26 },
        height: { xs: 22, md: 26 },
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f7f7ff 100%)',
        border: '1px solid rgba(87, 87, 176, 0.28)',
        color: '#4f46a5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 12px rgba(87, 87, 176, 0.14), inset 0 1px 1px rgba(255, 255, 255, 0.8)',
      }}>
        <ArrowForwardRoundedIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
      </Box>
    </Box>
  </Box>
);

const WorkflowJoinConnector = ({ canJoinPrevious, onJoinPrevious, label, tooltip }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hoverLeaveTimeoutRef = useRef(null);
  const isVisible = canJoinPrevious && isHovered;

  useEffect(() => () => {
    if (hoverLeaveTimeoutRef.current) {
      window.clearTimeout(hoverLeaveTimeoutRef.current);
    }
  }, []);

  const clearHoverLeaveTimeout = () => {
    if (hoverLeaveTimeoutRef.current) {
      window.clearTimeout(hoverLeaveTimeoutRef.current);
      hoverLeaveTimeoutRef.current = null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        onPointerEnter={(event) => {
          if (event.pointerType !== 'mouse') {
            return;
          }
          clearHoverLeaveTimeout();
          setIsHovered(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== 'mouse') {
            return;
          }
          clearHoverLeaveTimeout();
          hoverLeaveTimeoutRef.current = window.setTimeout(() => {
            setIsHovered(false);
            hoverLeaveTimeoutRef.current = null;
          }, 80);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <WorkflowConnector />
      </Box>
      {canJoinPrevious && (
        <AppTooltip title={tooltip} placement="top" arrow>
          <Button
            onPointerEnter={(event) => {
              if (event.pointerType !== 'mouse') {
                return;
              }
              clearHoverLeaveTimeout();
              setIsHovered(true);
            }}
            onPointerLeave={(event) => {
              if (event.pointerType !== 'mouse') {
                return;
              }
              clearHoverLeaveTimeout();
              hoverLeaveTimeoutRef.current = window.setTimeout(() => {
                setIsHovered(false);
                hoverLeaveTimeoutRef.current = null;
              }, 80);
            }}
            size="small"
            variant="outlined"
            startIcon={<CallSplitIcon sx={{ fontSize: 15 }} />}
            onClick={(event) => {
              event.stopPropagation();
              onJoinPrevious();
            }}
            sx={{
              mt: '-8px',
              px: 1.1,
              py: 0.3,
              minWidth: 0,
              borderRadius: '999px',
              borderColor: 'rgba(87, 87, 176, 0.45)',
              backgroundColor: '#ffffff',
              color: '#5757b0',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.4,
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(87, 87, 176, 0.14)',
              opacity: isVisible ? 1 : 0,
              pointerEvents: isVisible ? 'auto' : 'none',
              transform: isVisible ? 'translateY(0px)' : 'translateY(-4px)',
              transition: 'opacity 160ms ease, transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease, border-color 160ms ease',
              '& .MuiButton-startIcon': {
                mr: 0.45,
              },
              '&:hover': {
                borderColor: 'rgba(87, 87, 176, 0.72)',
                backgroundColor: 'rgba(87, 87, 176, 0.06)',
                boxShadow: '0 4px 12px rgba(87, 87, 176, 0.2)',
              },
              '@media (hover: none), (pointer: coarse)': {
                display: 'none',
              },
            }}
          >
            {label}
          </Button>
        </AppTooltip>
      )}
    </Box>
  );
};

const ActionsDataflowList = ({ caseid, autoOpenExportId, onExportOpened }) => {
  const { t, i18n } = useTranslation();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();
  // State management
  const [settings, setSettings] = useState([]);
  const [selectedSettingId, setSelectedSettingId] = useState(null);
  const [selectedSettingType, setSelectedSettingType] = useState(null);
  const [newActionDraft, setNewActionDraft] = useState(null);
  const [pendingCopyAction, setPendingCopyAction] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [replayConfirmOpen, setReplayConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSettingDialogOpen, setIsSettingDialogOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [actionSearchQuery, setActionSearchQuery] = useState('');
  const [canvasViewport, setCanvasViewport] = useState(() => ({ ...WORKFLOW_CANVAS_DEFAULT_VIEWPORT }));
  const [canvasNodePositions, setCanvasNodePositions] = useState({});
  const [runningWorkflows, setRunningWorkflows] = useState(new Set());
  const [isReplayingAllWorkflows, setIsReplayingAllWorkflows] = useState(false);
  const [timeeventCount, setTimeeventCount] = useState(null);
  const [isTimeeventCountLoading, setIsTimeeventCountLoading] = useState(false);
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [replayTimeoutId, setReplayTimeoutId] = useState(null);
  const [currentProcessingStep, setCurrentProcessingStep] = useState(null);
  const [processedSteps, setProcessedSteps] = useState(new Set());
  const [startedSteps, setStartedSteps] = useState(new Set());
  const [togglingScope, setTogglingScope] = useState(null); // Track which action is toggling scope
  const [workflowError, setWorkflowError] = useState(null); // Track workflow execution errors
  const [isLoading, setIsLoading] = useState(true); // Track initial loading state
  const [isWorkflowFlowsLoading, setIsWorkflowFlowsLoading] = useState(false);
  const [hasRequestedTags, setHasRequestedTags] = useState(false);
  const [pluginActions, setPluginActions] = useState([]);
  const [flowAutomationDialogOpen, setFlowAutomationDialogOpen] = useState(false);
  const [flowAutomationDialogMode, setFlowAutomationDialogMode] = useState('create');
  const [flowAutomationDialogFlowId, setFlowAutomationDialogFlowId] = useState(null);
  const [flowAutomationDialogName, setFlowAutomationDialogName] = useState('');
  const [flowAutomationDialogTriggerMode, setFlowAutomationDialogTriggerMode] = useState('event');
  const [flowAutomationDialogTriggerFilter, setFlowAutomationDialogTriggerFilter] = useState(DEFAULT_FLOW_TRIGGER_FILTER);
  const [flowAutomationDialogFrequencyValue, setFlowAutomationDialogFrequencyValue] = useState('1');
  const [flowAutomationDialogFrequencyUnit, setFlowAutomationDialogFrequencyUnit] = useState('minutes');
  const [flowAutomationDialogScheduleDayOfWeek, setFlowAutomationDialogScheduleDayOfWeek] = useState([]);
  const [flowAutomationDialogScheduleDayOfMonth, setFlowAutomationDialogScheduleDayOfMonth] = useState([]);
  const [flowAutomationDialogScheduleMonth, setFlowAutomationDialogScheduleMonth] = useState([]);
  const [flowAutomationDialogScheduleHour, setFlowAutomationDialogScheduleHour] = useState([]);
  const [flowAutomationDialogScheduleMinute, setFlowAutomationDialogScheduleMinute] = useState([]);
  const [flowAutomationDialogScheduleTimezone, setFlowAutomationDialogScheduleTimezone] = useState(getBrowserTimezone());
  const [flowAutomationDialogError, setFlowAutomationDialogError] = useState('');
  const [flowAutomationDialogSubmitting, setFlowAutomationDialogSubmitting] = useState(false);
  const [flowRenameDialogOpen, setFlowRenameDialogOpen] = useState(false);
  const [flowRenameDialogValue, setFlowRenameDialogValue] = useState('');
  const [flowRenameDialogError, setFlowRenameDialogError] = useState('');
  const [flowDeleteDialogOpen, setFlowDeleteDialogOpen] = useState(false);
  const [templateDecisionDialogOpen, setTemplateDecisionDialogOpen] = useState(false);
  const [templateDecisionData, setTemplateDecisionData] = useState(null);
  const [scopeChangeDialogOpen, setScopeChangeDialogOpen] = useState(false);
  const [canvasResetConfirmOpen, setCanvasResetConfirmOpen] = useState(false);
  const [pendingScopeChange, setPendingScopeChange] = useState(null);
  const [pendingTemplateMutation, setPendingTemplateMutation] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const canvasInteractionRef = useRef(null);
  const ignoredNodeClickRef = useRef(null);
  const canvasNodePositionsRef = useRef({});
  const canvasContainerRef = useRef(null);
  const lastCenteredSearchRef = useRef(null);

  const isFlowRunLimitExceeded = Number.isFinite(timeeventCount)
    && timeeventCount > MAX_MANUAL_FLOW_RUN_TIMEEVENTS;
  const isFlowRunCountUnavailable = !isTimeeventCountLoading && timeeventCount === null;
  const isFlowRunDisabled = isReplayingAllWorkflows
    || isTimeeventCountLoading
    || isFlowRunCountUnavailable
    || isFlowRunLimitExceeded;

  // Memoized values
  const menuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);
  const nameMap = useMemo(() => getNameMap(t), [t]);
  const pluginTypeSet = useMemo(
    () => new Set((pluginActions || []).map((plugin) => plugin?.type).filter(Boolean)),
    [pluginActions]
  );
  const pluginNameByType = useMemo(() => {
    const names = {};
    (pluginActions || []).forEach((plugin) => {
      if (plugin?.type) {
        names[plugin.type] = plugin.name || plugin.type;
      }
    });
    return names;
  }, [pluginActions]);
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const calendarWeekdayOptions = useMemo(
    () => [0, 1, 2, 3, 4, 5, 6].map((value) => ({
      value,
      label: formatFlowWeekdayLabel(value, i18n.resolvedLanguage || i18n.language)
    })),
    [i18n.language, i18n.resolvedLanguage]
  );
  const calendarDayOfMonthOptions = useMemo(
    () => Array.from({ length: 31 }, (_, index) => {
      const value = index + 1;
      return {
        value,
        label: String(value)
      };
    }),
    []
  );
  const calendarMonthOptions = useMemo(
    () => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((value) => ({
      value,
      label: formatFlowMonthLabel(value, i18n.resolvedLanguage || i18n.language)
    })),
    [i18n.language, i18n.resolvedLanguage]
  );
  const calendarHourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, index) => {
      const value = index;
      return {
        value,
        label: String(value).padStart(2, '0')
      };
    }),
    []
  );
  const calendarMinuteOptions = useMemo(
    () => Array.from({ length: 60 }, (_, index) => {
      const value = index;
      return {
        value,
        label: String(value).padStart(2, '0')
      };
    }),
    []
  );
  const calendarWeekdayLabelMap = useMemo(() => createFlowScheduleLabelMap(calendarWeekdayOptions), [calendarWeekdayOptions]);
  const calendarDayOfMonthLabelMap = useMemo(() => createFlowScheduleLabelMap(calendarDayOfMonthOptions), [calendarDayOfMonthOptions]);
  const calendarMonthLabelMap = useMemo(() => createFlowScheduleLabelMap(calendarMonthOptions), [calendarMonthOptions]);
  const calendarHourLabelMap = useMemo(() => createFlowScheduleLabelMap(calendarHourOptions), [calendarHourOptions]);
  const calendarMinuteLabelMap = useMemo(() => createFlowScheduleLabelMap(calendarMinuteOptions), [calendarMinuteOptions]);
  const currentSettingForDialog = useMemo(() => {
    const existingSetting = settings.find(s => s.id === selectedSettingId);
    if (existingSetting) {
      if (isTemporaryPlaceholderName(existingSetting.name)) {
        return {
          ...existingSetting,
          name: pluginNameByType[existingSetting.type]
            || nameMap[existingSetting.type]
            || existingSetting.name
        };
      }
      return existingSetting;
    }

    if (newActionDraft && newActionDraft.id === selectedSettingId) {
      return newActionDraft;
    }

    // If setting not found in list and we have a selectedSettingType, create a temporary setting for the dialog
    if (selectedSettingType && selectedSettingId) {
      return {
        id: selectedSettingId,
        type: selectedSettingType,
        enable: 0,
        caseid: caseid,
        interval: '0',
        name: pluginNameByType[selectedSettingType] || t(`settings.new_${selectedSettingType}_name`, `New ${selectedSettingType.charAt(0).toUpperCase() + selectedSettingType.slice(1)}`),
        scope: 'event', // Default scope for fallback
        flow_id: selectedFlowId ?? (flows[0]?.id ?? null),
        data: {}
      };
    }

    return null;
  }, [settings, selectedSettingId, selectedSettingType, newActionDraft, caseid, t, pluginNameByType, selectedFlowId, flows]);

  // Memoized filtered and processed settings
  const processedSettings = useMemo(() => {
    return settings.map(setting => ({
      ...setting,
      isEnabled: isActionEnabled(setting.enable),
      name: getSettingName(setting, nameMap, pluginNameByType),
      iconSrc: getSettingIcon(setting.type)
    }));
  }, [settings, nameMap, pluginNameByType]);

  const sortedFlows = useMemo(() => {
    return [...(flows || [])].sort((a, b) => {
      const orderA = Number.isFinite(Number(a?.orderIndex)) ? Number(a.orderIndex) : 0;
      const orderB = Number.isFinite(Number(b?.orderIndex)) ? Number(b.orderIndex) : 0;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return Number(a?.id || 0) - Number(b?.id || 0);
    });
  }, [flows]);

  const fallbackFlowId = useMemo(() => {
    if (selectedFlowId !== null && selectedFlowId !== undefined) {
      return selectedFlowId;
    }
    return sortedFlows[0]?.id ?? null;
  }, [selectedFlowId, sortedFlows]);

  const resolveSettingFlowId = useCallback((setting) => {
    if (!setting) return fallbackFlowId;
    if (setting.flow_id !== undefined && setting.flow_id !== null) {
      return setting.flow_id;
    }
    if (setting.flowId !== undefined && setting.flowId !== null) {
      return setting.flowId;
    }
    return sortedFlows[0]?.id ?? fallbackFlowId;
  }, [sortedFlows, fallbackFlowId]);

  const selectedFlowSettings = useMemo(() => {
    if (!processedSettings.length) {
      return [];
    }
    if (fallbackFlowId === null || fallbackFlowId === undefined) {
      return processedSettings;
    }
    return processedSettings.filter((setting) => String(resolveSettingFlowId(setting)) === String(fallbackFlowId));
  }, [processedSettings, fallbackFlowId, resolveSettingFlowId]);

  const selectedFlowExecutionStages = useMemo(
    () => buildActionExecutionStages(selectedFlowSettings),
    [selectedFlowSettings]
  );

  const selectedFlow = useMemo(
    () => sortedFlows.find((flow) => String(flow.id) === String(fallbackFlowId)) || null,
    [sortedFlows, fallbackFlowId]
  );

  const dialogTags = useMemo(() => {
    const baseTags = Array.isArray(tags) ? tags : [];
    const dialogFlowId = parsePositiveInteger(
      currentSettingForDialog?.flow_id ?? currentSettingForDialog?.flowId ?? fallbackFlowId
    );

    if (!dialogFlowId) {
      return baseTags;
    }

    const loopTagNames = [...new Set(
      settings
        .filter((setting) => getLoopTriggerTargetFlowId(setting) === dialogFlowId)
        .map((setting) => getLoopTriggerTagName(setting))
        .filter(Boolean)
    )];

    if (loopTagNames.length === 0) {
      return baseTags;
    }

    return [
      ...baseTags,
      ...loopTagNames
        .filter((tagName) => !hasTagDefinition(baseTags, tagName))
        .map((tagName) => ({
          variable: tagName,
          label: tagName,
          description: t(
            'workflow.looptriggerflow.loop_tag_description',
            'Current item sent by Find Items and Repeat Flow.'
          )
        }))
    ];
  }, [tags, currentSettingForDialog, fallbackFlowId, settings, t]);

  const normalizedActionSearchQuery = actionSearchQuery.trim().toLowerCase();
  const actionSearchMatchIds = useMemo(() => {
    if (!normalizedActionSearchQuery) {
      return new Set();
    }
    return new Set(
      selectedFlowSettings
        .filter((setting) => getActionSearchText(setting).includes(normalizedActionSearchQuery))
        .map((setting) => String(setting.id))
    );
  }, [normalizedActionSearchQuery, selectedFlowSettings]);
  const actionSearchMatchCount = normalizedActionSearchQuery ? actionSearchMatchIds.size : selectedFlowSettings.length;
  const uniqueActionSearchMatchId = actionSearchMatchIds.size === 1
    ? Array.from(actionSearchMatchIds)[0]
    : null;

  const isSelectedFlowMain = useMemo(() => {
    if (!selectedFlow) {
      return false;
    }
    if (Number(selectedFlow.is_main) === 1) {
      return true;
    }
    const normalizedName = String(selectedFlow.name || '').trim().toLowerCase();
    return normalizedName === 'main flow';
  }, [selectedFlow]);

  const selectedFlowTriggerMode = useMemo(() => {
    if (isSelectedFlowMain) {
      return 'event';
    }
    return normalizeFlowTriggerMode(selectedFlow?.trigger_mode, selectedFlow?.auto_run_enabled);
  }, [selectedFlow, isSelectedFlowMain]);

  const selectedFlowTriggerModeLabel = useMemo(() => {
    if (!selectedFlow) {
      return '';
    }
    if (selectedFlowTriggerMode === 'disabled') {
      return t('workflow.automation_mode_disabled', 'Disabled');
    }
    if (selectedFlowTriggerMode === 'event') {
      return t('workflow.automation_trigger_on_event', 'New or updated event');
    }
    if (selectedFlowTriggerMode === 'event_confirmed') {
      return t('workflow.automation_trigger_on_event_confirmed', 'When event is confirmed');
    }
    if (selectedFlowTriggerMode === 'event_change') {
      return t('workflow.automation_trigger_on_event_change', 'On event detail change');
    }
    if (selectedFlowTriggerMode === 'calendar') {
      const scheduleDayOfWeek = normalizeFlowScheduleDayOfWeek(selectedFlow.auto_run_schedule_day_of_week);
      const scheduleDayOfMonth = normalizeFlowScheduleDayOfMonth(selectedFlow.auto_run_schedule_day_of_month);
      const scheduleMonth = normalizeFlowScheduleMonth(selectedFlow.auto_run_schedule_month);
      const scheduleHour = normalizeFlowScheduleHour(selectedFlow.auto_run_schedule_hour);
      const scheduleMinute = normalizeFlowScheduleMinute(selectedFlow.auto_run_schedule_minute);
      const scheduleTimezone = normalizeFlowScheduleTimezone(selectedFlow.auto_run_schedule_timezone || browserTimezone);
      const summaryParts = [];
      if (scheduleDayOfWeek.length) {
        summaryParts.push(
          `${t('workflow.automation_weekdays_label', 'Weekdays')}: ${formatFlowScheduleSelectionList(scheduleDayOfWeek, calendarWeekdayLabelMap)}`
        );
      }
      if (scheduleDayOfMonth.length) {
        summaryParts.push(
          `${t('workflow.automation_days_of_month_label', 'Days of month')}: ${formatFlowScheduleSelectionList(scheduleDayOfMonth, calendarDayOfMonthLabelMap)}`
        );
      }
      if (scheduleMonth.length) {
        summaryParts.push(
          `${t('workflow.automation_months_label', 'Months')}: ${formatFlowScheduleSelectionList(scheduleMonth, calendarMonthLabelMap)}`
        );
      }
      if (scheduleHour.length) {
        summaryParts.push(
          `${t('workflow.automation_hours_label', 'Hours')}: ${formatFlowScheduleSelectionList(scheduleHour, calendarHourLabelMap)}`
        );
      }
      if (scheduleMinute.length) {
        summaryParts.push(
          `${t('workflow.automation_minutes_label', 'Minutes')}: ${formatFlowScheduleSelectionList(scheduleMinute, calendarMinuteLabelMap)}`
        );
      }
      if (!summaryParts.length) {
        summaryParts.push(t('workflow.automation_calendar_every_minute', 'Every minute'));
      }
      return t('workflow.automation_calendar_schedule_summary', 'Calendar: {{schedule}} ({{timezone}})', {
        schedule: summaryParts.join('; '),
        timezone: scheduleTimezone
      });
    }
    const frequencyValue = Math.max(1, Number(selectedFlow.auto_run_frequency_value || 1));
    const unit = normalizeFlowAutomationUnit(selectedFlow.auto_run_frequency_unit);
    const unitLabelMap = {
      minutes: t('workflow.automation_unit_minutes', 'minutes'),
      hours: t('workflow.automation_unit_hours', 'hours'),
      days: t('workflow.automation_unit_days', 'days')
    };
    return t('workflow.automation_every', 'Auto every {{value}} {{unit}}', {
      value: frequencyValue,
      unit: unitLabelMap[unit] || unit
    });
  }, [
    selectedFlow,
    selectedFlowTriggerMode,
    t,
    browserTimezone,
    i18n.language,
    i18n.resolvedLanguage,
    calendarWeekdayLabelMap,
    calendarDayOfMonthLabelMap,
    calendarMonthLabelMap,
    calendarHourLabelMap,
    calendarMinuteLabelMap
  ]);

  const selectedFlowTriggerVisual = useMemo(
    () => getTriggerVisualConfig(selectedFlowTriggerMode),
    [selectedFlowTriggerMode]
  );
  const TriggerModeIcon = selectedFlowTriggerVisual.icon;

  const showFlowTriggerNode = useMemo(
    () => Boolean(selectedFlow),
    [selectedFlow]
  );

  const canvasFlowKey = fallbackFlowId !== null && fallbackFlowId !== undefined
    ? String(fallbackFlowId)
    : 'default';

  useEffect(() => {
    setCanvasViewport({ ...WORKFLOW_CANVAS_DEFAULT_VIEWPORT });
  }, [canvasFlowKey]);

  const canvasBaseLayout = useMemo(() => {
    const nodes = [];
    const edges = [];
    const positions = {};
    const stageNodeIds = [];
    let stageIndexOffset = 0;

    if (showFlowTriggerNode) {
      positions[WORKFLOW_TRIGGER_NODE_ID] = {
        x: WORKFLOW_CANVAS_START_X,
        y: WORKFLOW_CANVAS_START_Y
      };
      stageNodeIds.push([WORKFLOW_TRIGGER_NODE_ID]);
      nodes.push({ id: WORKFLOW_TRIGGER_NODE_ID, kind: 'trigger' });
      stageIndexOffset = 1;
    }

    selectedFlowExecutionStages.forEach((stage, stageIndex) => {
      const stageActionIds = stage.actions.map((setting) => `action:${setting.id}`);
      const stageX = WORKFLOW_CANVAS_START_X + (stageIndex + stageIndexOffset) * WORKFLOW_CANVAS_STAGE_GAP;
      const stageHeight = Math.max(0, (stageActionIds.length - 1) * WORKFLOW_CANVAS_PARALLEL_GAP);
      const stageStartY = WORKFLOW_CANVAS_START_Y - stageHeight / 2;

      stage.actions.forEach((setting, actionIndex) => {
        const nodeId = `action:${setting.id}`;
        positions[nodeId] = {
          x: stageX,
          y: stageStartY + actionIndex * WORKFLOW_CANVAS_PARALLEL_GAP
        };
        nodes.push({ id: nodeId, kind: 'action', setting });
      });

      stageNodeIds.push(stageActionIds);
    });

    const addNodeX = WORKFLOW_CANVAS_START_X + (selectedFlowExecutionStages.length + stageIndexOffset) * WORKFLOW_CANVAS_STAGE_GAP;
    positions[WORKFLOW_ADD_NODE_ID] = {
      x: addNodeX,
      y: WORKFLOW_CANVAS_START_Y
    };
    nodes.push({ id: WORKFLOW_ADD_NODE_ID, kind: 'add' });
    stageNodeIds.push([WORKFLOW_ADD_NODE_ID]);

    for (let index = 0; index < stageNodeIds.length - 1; index += 1) {
      const sourceIds = stageNodeIds[index] || [];
      const targetIds = stageNodeIds[index + 1] || [];
      sourceIds.forEach((sourceId) => {
        targetIds.forEach((targetId) => {
          edges.push({ sourceId, targetId });
        });
      });
    }

    return { nodes, edges, positions };
  }, [selectedFlowExecutionStages, showFlowTriggerNode]);

  const canvasNodePositionOverrides = useMemo(
    () => canvasNodePositions[canvasFlowKey] || {},
    [canvasFlowKey, canvasNodePositions]
  );
  const getCanvasNodePosition = useCallback((nodeId) => {
    return canvasNodePositionOverrides[nodeId] || canvasBaseLayout.positions[nodeId] || { x: 0, y: 0 };
  }, [canvasBaseLayout.positions, canvasNodePositionOverrides]);

  useEffect(() => {
    if (!uniqueActionSearchMatchId || !canvasContainerRef.current) {
      if (!uniqueActionSearchMatchId) {
        lastCenteredSearchRef.current = null;
      }
      return;
    }

    const searchKey = `${canvasFlowKey}:${normalizedActionSearchQuery}:${uniqueActionSearchMatchId}`;
    if (lastCenteredSearchRef.current === searchKey) {
      return;
    }

    const canvasRect = canvasContainerRef.current.getBoundingClientRect();
    if (!canvasRect.width || !canvasRect.height) {
      return;
    }

    const nodePosition = getCanvasNodePosition(`action:${uniqueActionSearchMatchId}`);
    const scale = canvasViewport.scale;
    setCanvasViewport((previousViewport) => ({
      ...previousViewport,
      x: (canvasRect.width / 2) - ((nodePosition.x + (WORKFLOW_CANVAS_NODE_WIDTH / 2)) * scale),
      y: (canvasRect.height / 2) - ((nodePosition.y + (WORKFLOW_CANVAS_NODE_HEIGHT / 2)) * scale)
    }));
    lastCenteredSearchRef.current = searchKey;
  }, [
    canvasFlowKey,
    canvasViewport.scale,
    getCanvasNodePosition,
    normalizedActionSearchQuery,
    uniqueActionSearchMatchId
  ]);

  const canvasBounds = useMemo(() => {
    const positions = canvasBaseLayout.nodes.map((node) => getCanvasNodePosition(node.id));
    if (positions.length === 0) {
      return { width: 900, height: 460 };
    }
    const maxX = Math.max(...positions.map((position) => position.x));
    const maxY = Math.max(...positions.map((position) => position.y));
    return {
      width: Math.max(900, maxX + WORKFLOW_CANVAS_NODE_WIDTH + 180),
      height: Math.max(460, maxY + WORKFLOW_CANVAS_NODE_HEIGHT + 130)
    };
  }, [canvasBaseLayout.nodes, getCanvasNodePosition]);

  const syncWorkflowFlows = useCallback((flowList, preferredFlowId = null) => {
    const normalizedFlows = Array.isArray(flowList) ? flowList : [];
    setFlows(normalizedFlows);
    setSelectedFlowId((currentSelectedFlowId) => {
      if (preferredFlowId !== null && preferredFlowId !== undefined) {
        return preferredFlowId;
      }
      if (normalizedFlows.length === 0) {
        return null;
      }
      if (
        currentSelectedFlowId !== null &&
        normalizedFlows.some((flow) => String(flow.id) === String(currentSelectedFlowId))
      ) {
        return currentSelectedFlowId;
      }
      return normalizedFlows[0].id;
    });
  }, []);

  const loadWorkflowFlows = useCallback(() => {
    if (!caseid) {
      canvasNodePositionsRef.current = {};
      setCanvasNodePositions({});
      syncWorkflowFlows([]);
      setIsWorkflowFlowsLoading(false);
      setIsLoading(false);
      return Promise.resolve([]);
    }

    setIsWorkflowFlowsLoading(true);
    return fetchProjectWorkflowFlows(caseid)
      .then((payload) => {
        const flowList = Array.isArray(payload?.flows) ? payload.flows : [];
        const storedPositions = {};
        flowList.forEach((flow) => {
          const flowId = flow?.id;
          const nodes = flow?.canvas_layout?.nodes;
          if (flowId !== undefined && flowId !== null && nodes && typeof nodes === 'object' && !Array.isArray(nodes)) {
            storedPositions[String(flowId)] = nodes;
          }
        });
        canvasNodePositionsRef.current = storedPositions;
        setCanvasNodePositions(storedPositions);
        syncWorkflowFlows(flowList);
        return flowList;
      })
      .catch((error) => {
        console.error('Failed to load workflow flows:', error);
        syncWorkflowFlows([]);
        return [];
      })
      .finally(() => {
        setIsWorkflowFlowsLoading(false);
      });
  }, [caseid, syncWorkflowFlows]);

  useEffect(() => {
    if (ws && caseid) {
      setIsLoading(true);
      setTags([]);
      setHasRequestedTags(false);
      setFlows([]);
      setSelectedFlowId(null);
      sendMessage({ command: 'getActionPlugins', caseid });
      sendMessage({ command: 'getListExportsSettings', caseid });
      loadWorkflowFlows();
    }
  }, [ws, caseid, loadWorkflowFlows, sendMessage]);

  useEffect(() => {
    let cancelled = false;
    if (!caseid) {
      setTimeeventCount(null);
      setIsTimeeventCountLoading(false);
      return undefined;
    }

    setTimeeventCount(null);
    setIsTimeeventCountLoading(true);
    fetchTimeeventCount(caseid)
      .then((payload) => {
        if (cancelled) return;
        const total = Number(payload?.total);
        setTimeeventCount(Number.isFinite(total) ? total : null);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(`Failed to load time event count for project ${caseid}:`, error);
          setTimeeventCount(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsTimeeventCountLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [caseid]);

  // Load tags lazily only when action settings dialog is opened.
  useEffect(() => {
    if (!ws || !caseid || !isSettingDialogOpen || hasRequestedTags) {
      return;
    }
    if (sendMessage) {
      sendMessage({ command: 'get_all_unique_tags', caseid });
    }
    setHasRequestedTags(true);
  }, [ws, caseid, isSettingDialogOpen, hasRequestedTags, sendMessage]);

  // Fallback: clear loading after a reasonable time if server doesn't reply
  useEffect(() => {
    if (!isLoading) return undefined;
    const fallback = setTimeout(() => {
      setIsLoading(false);
    }, 12000);
    return () => clearTimeout(fallback);
  }, [isLoading]);

  // Auto-open action dialog after OAuth redirect
  useEffect(() => {
    if (autoOpenExportId && settings.length > 0 && !isSettingDialogOpen) {
      const setting = settings.find(s => String(s.id) === String(autoOpenExportId));
      if (setting) {
        setSelectedSettingId(setting.id);
        setSelectedSettingType(setting.type);
        if (setting.flow_id !== undefined && setting.flow_id !== null) {
          setSelectedFlowId(setting.flow_id);
        }
        setIsSettingDialogOpen(true);
        if (onExportOpened) {
          onExportOpened();
        }
      }
    }
  }, [autoOpenExportId, settings, isSettingDialogOpen, onExportOpened]);
  // Optimized WebSocket message handlers
  const handleGetListExportSettings = useCallback((parsedMessage) => {
    const serverSettingsArray = parsedMessage.data || [];
    if (!Array.isArray(serverSettingsArray)) {
      console.error('getListExportSettings: data is not an array', parsedMessage.data);
      return;
    }

    const filteredSettingsFromServer = filterSupportedSettings(serverSettingsArray, pluginTypeSet);
    setSettings(prevSettings => {
      const localSettingsMap = new Map(prevSettings.map((setting) => [setting.id, setting]));
      const resultSettings = filteredSettingsFromServer.map((serverSetting) => {
        const localSetting = localSettingsMap.get(serverSetting.id);
        if (localSetting && localSetting.id === selectedSettingId && isSettingDialogOpen) {
          return { ...serverSetting, ...localSetting };
        }
        return serverSetting;
      });

      // Update running workflows based on enabled settings
      setRunningWorkflows(prev => {
        const newRunning = new Set();
        resultSettings.forEach(setting => {
          if (isActionEnabled(setting.enable)) {
            newRunning.add(setting.id);
          }
        });
        return newRunning;
      });

      return resultSettings;
    });

    // Clear loading state once data is received
    setIsLoading(false);
  }, [selectedSettingId, isSettingDialogOpen, pluginTypeSet]);

  const handleSaveActionSettings = useCallback((parsedMessage) => {
    const savedSetting = parsedMessage.data;
    const settingType = savedSetting.type;

    setSettings(prev => {
      const updatedSettings = prev.filter(s => {
        if (isTemporarySettingId(s.id) && selectedSettingId === s.id) {
          return false;
        }
        return true;
      });

      const existingIndex = updatedSettings.findIndex(s => s.id === savedSetting.id);
      const existingSetting = existingIndex >= 0 ? updatedSettings[existingIndex] : null;
      const resolvedScope = savedSetting.scope || existingSetting?.scope || newActionDraft?.scope;
      const resolvedFlowId = savedSetting.flow_id
        ?? savedSetting.flowId
        ?? existingSetting?.flow_id
        ?? existingSetting?.flowId
        ?? newActionDraft?.flow_id
        ?? newActionDraft?.flowId;
      const resolvedTargetExecutionMode = savedSetting.target_execution_mode
        ?? existingSetting?.target_execution_mode
        ?? newActionDraft?.target_execution_mode;
      const resolvedMaxParallelTargets = savedSetting.max_parallel_targets
        ?? existingSetting?.max_parallel_targets
        ?? newActionDraft?.max_parallel_targets;

      if (existingIndex >= 0) {
        const fallbackName = pluginNameByType[savedSetting.type] || `New ${savedSetting.type || 'Flow'}`;
        updatedSettings[existingIndex] = {
          ...savedSetting,
          scope: resolvedScope,
          flow_id: resolvedFlowId,
          target_execution_mode: resolvedTargetExecutionMode,
          max_parallel_targets: resolvedMaxParallelTargets,
          name: isTemporaryPlaceholderName(savedSetting.name)
            ? (updatedSettings[existingIndex].name && !isTemporaryPlaceholderName(updatedSettings[existingIndex].name)
              ? updatedSettings[existingIndex].name
              : fallbackName)
            : (savedSetting.name || updatedSettings[existingIndex].name || fallbackName),
          data: {
            ...updatedSettings[existingIndex].data,
            ...savedSetting.customData
          }
        };
      } else {
        const fallbackName = pluginNameByType[savedSetting.type] || `New ${savedSetting.type || 'Flow'}`;
        const newSetting = {
          ...savedSetting,
          scope: resolvedScope,
          flow_id: resolvedFlowId,
          target_execution_mode: resolvedTargetExecutionMode,
          max_parallel_targets: resolvedMaxParallelTargets,
          name: isTemporaryPlaceholderName(savedSetting.name)
            ? fallbackName
            : (savedSetting.name || fallbackName),
          data: savedSetting.customData || {}
        };
        updatedSettings.push(newSetting);
      }
      return updatedSettings;
    });

    const hasNewActionDraft = Boolean(
      newActionDraft
      && isTemporarySettingId(newActionDraft.id)
      && selectedSettingId === newActionDraft.id
    );

    if (hasNewActionDraft) {
      setNewActionDraft((prevDraft) => {
        if (!prevDraft) {
          return prevDraft;
        }

        return {
          ...prevDraft,
          ...savedSetting,
          id: savedSetting.id,
          scope: savedSetting.scope || prevDraft.scope,
          flow_id: savedSetting.flow_id
            ?? savedSetting.flowId
            ?? prevDraft.flow_id
            ?? prevDraft.flowId,
          target_execution_mode: savedSetting.target_execution_mode || prevDraft.target_execution_mode,
          max_parallel_targets: savedSetting.max_parallel_targets || prevDraft.max_parallel_targets,
          data: {
            ...(savedSetting.customData || savedSetting.data || {}),
            ...(prevDraft.data || {})
          }
        };
      });
    }

    setSelectedSettingId(savedSetting.id);
    setSelectedSettingType(settingType);

    if (
      pendingCopyAction
      && pendingCopyAction.sourceType === savedSetting.type
      && savedSetting.id
      && String(savedSetting.id) !== String(pendingCopyAction.sourceId)
    ) {
      const copySuffix = t('sidebar.copy_suffix', ' (Copy)');
      const copiedName = `${pendingCopyAction.sourceName || getSettingName(savedSetting, nameMap, pluginNameByType)}${copySuffix}`;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          command: 'flowRename',
          id: savedSetting.id,
          name: copiedName
        }));
      }
    }

    if (caseid) {
      sendMessage({
        command: 'getListExportsSettings',
        caseid
      });
    }
  }, [newActionDraft, selectedSettingId, caseid, pendingCopyAction, t, nameMap, pluginNameByType, sendMessage]);

  const sendTemplateAwareCommand = useCallback((payload) => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !payload?.command) {
      return false;
    }

    if (TEMPLATE_GUARDED_COMMANDS.has(payload.command)) {
      setPendingTemplateMutation(payload);
    }

    sendMessage(payload);
    return true;
  }, [sendMessage]);

  const handleTemplateChangeDecisionRequired = useCallback((parsedMessage) => {
    setTemplateDecisionData(parsedMessage?.data || {});
    setTemplateDecisionDialogOpen(true);
  }, []);

  const handleTemplatePropagationJobStatus = useCallback((parsedMessage) => {
    const payload = parsedMessage?.data || {};
    const job = payload.job || {};
    const totalCases = job.total_cases ?? 0;
    const isLazyMode = job.mode === 'lazy_template_managed';

    if (payload.status === 'queued') {
      setSnackbar({
        open: true,
        message: t('template.propagation_started', `Template propagation started for ${totalCases} linked projects.`),
        severity: 'info'
      });
      return;
    }

    if (payload.status === 'completed') {
      setSnackbar({
        open: true,
        message: t(
          isLazyMode ? 'template.propagation_marked_stale' : 'template.propagation_completed',
          isLazyMode
            ? `Template updated. ${job.success_cases ?? 0} linked projects will refresh actions, flows, and widgets automatically on next open or run.`
            : `Template propagation completed. Updated ${job.success_cases ?? 0} projects, failed ${job.failed_cases ?? 0}.`
        ),
        severity: (job.failed_cases ?? 0) > 0 ? 'warning' : 'success'
      });
      return;
    }

    if (payload.status === 'failed') {
      setSnackbar({
        open: true,
        message: payload.message || t('template.propagation_failed', 'Template propagation failed.'),
        severity: 'error'
      });
    }
  }, [t]);

  const handleTemplateDetachSuccess = useCallback(() => {
    setSnackbar({
      open: true,
      message: t('template.project_detached', 'Project detached from template.'),
      severity: 'info'
    });
  }, [t]);

  const handleCloseTemplateDecisionDialog = useCallback(() => {
    setTemplateDecisionDialogOpen(false);
    setTemplateDecisionData(null);
    setPendingTemplateMutation(null);
  }, []);

  const handleCancelTemplateDecision = useCallback(() => {
    handleCloseTemplateDecisionDialog();
    if (caseid) {
      sendMessage({ command: 'getListExportsSettings', caseid });
    }
    loadWorkflowFlows();
  }, [handleCloseTemplateDecisionDialog, caseid, loadWorkflowFlows, sendMessage]);

  const submitWorkflowFlowAutomationHttp = useCallback(async (payload) => {
    if (!caseid || !payload?.flow_id) {
      return false;
    }

    setPendingTemplateMutation(payload);
    const flowId = payload.flow_id;
    const requestPayload = { ...payload };
    delete requestPayload.command;
    delete requestPayload.flow_id;
    delete requestPayload.caseid;

    try {
      const response = await updateProjectWorkflowFlowAutomation(caseid, flowId, requestPayload);
      const messages = Array.isArray(response?.messages) ? response.messages : [];
      const decisionMessage = messages.find((message) => message?.command === 'templateChangeDecisionRequired');

      messages.forEach((message) => {
        if (message?.command === 'templateProjectDetached') {
          handleTemplateDetachSuccess();
        } else if (message?.command === 'templatePropagationJobStatus') {
          handleTemplatePropagationJobStatus(message);
        } else if (message?.command === 'getWorkflowFlows' && Array.isArray(message?.data?.flows)) {
          setFlows(message.data.flows);
        }
      });

      if (response?.status === 'decision_required' || decisionMessage) {
        setTemplateDecisionData(decisionMessage?.data || response);
        setTemplateDecisionDialogOpen(true);
        return 'decision_required';
      }

      const result = response?.result
        || messages.find((message) => message?.command === 'updateWorkflowFlowAutomationResponse')?.data
        || response;

      if (result?.status !== 'success') {
        throw new Error(result?.message || response?.message || 'Failed to update flow automation');
      }

      const updatedFlow = result.flow || response.flow || {};
      if (updatedFlow.id !== undefined && updatedFlow.id !== null) {
        setFlows((prev) => prev.map((flow) => (
          String(flow.id) === String(updatedFlow.id)
            ? { ...flow, ...updatedFlow }
            : flow
        )));
      } else if (!messages.some((message) => message?.command === 'getWorkflowFlows')) {
        loadWorkflowFlows();
      }

      setPendingTemplateMutation(null);
      setSnackbar({
        open: true,
        message: t('workflow.automation_saved', 'Automation saved.'),
        severity: 'success'
      });
      return true;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to update flow automation';
      console.error(`Failed to update flow automation: ${errorMessage}`, error);
      window.alert(errorMessage);
      loadWorkflowFlows();
      return false;
    }
  }, [
    caseid,
    handleTemplateDetachSuccess,
    handleTemplatePropagationJobStatus,
    loadWorkflowFlows,
    t
  ]);

  const submitWorkflowFlowCreateHttp = useCallback(async (payload) => {
    if (!caseid || !payload?.name) {
      return false;
    }

    setPendingTemplateMutation(payload);
    const requestPayload = { ...payload };
    delete requestPayload.command;
    delete requestPayload.caseid;

    try {
      const response = await createProjectWorkflowFlow(caseid, requestPayload);
      const messages = Array.isArray(response?.messages) ? response.messages : [];
      const decisionMessage = messages.find((message) => message?.command === 'templateChangeDecisionRequired');
      const workflowFlowsMessage = messages.find((message) => message?.command === 'getWorkflowFlowsResponse');

      messages.forEach((message) => {
        if (message?.command === 'templateProjectDetached') {
          handleTemplateDetachSuccess();
        } else if (message?.command === 'templatePropagationJobStatus') {
          handleTemplatePropagationJobStatus(message);
        }
      });

      if (response?.status === 'decision_required' || decisionMessage) {
        setTemplateDecisionData(decisionMessage?.data || response);
        setTemplateDecisionDialogOpen(true);
        return 'decision_required';
      }

      const result = response?.result
        || messages.find((message) => message?.command === 'createWorkflowFlowResponse')?.data
        || response;

      if (result?.status !== 'success') {
        throw new Error(result?.message || response?.message || 'Failed to create workflow flow');
      }

      const createdFlow = result.flow || response.flow || {};
      const createdFlowId = createdFlow.id !== undefined && createdFlow.id !== null
        ? createdFlow.id
        : null;
      const flowList = Array.isArray(response?.flows)
        ? response.flows
        : Array.isArray(workflowFlowsMessage?.data?.flows)
          ? workflowFlowsMessage.data.flows
          : null;

      if (Array.isArray(flowList)) {
        syncWorkflowFlows(flowList, createdFlowId);
      } else if (createdFlowId !== null) {
        setFlows((prev) => {
          const withoutDuplicate = prev.filter((flow) => String(flow.id) !== String(createdFlowId));
          return [...withoutDuplicate, createdFlow];
        });
        setSelectedFlowId(createdFlowId);
      } else {
        loadWorkflowFlows();
      }

      setPendingTemplateMutation(null);
      return response;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to create workflow flow';
      console.error(`Failed to create workflow flow: ${errorMessage}`, error);
      window.alert(errorMessage);
      setPendingTemplateMutation(null);
      loadWorkflowFlows();
      return false;
    }
  }, [
    caseid,
    handleTemplateDetachSuccess,
    handleTemplatePropagationJobStatus,
    loadWorkflowFlows,
    syncWorkflowFlows
  ]);

  const handleTemplateDecision = useCallback((mode) => {
    if (!pendingTemplateMutation) {
      handleCloseTemplateDecisionDialog();
      return;
    }

    if (pendingTemplateMutation.command === 'updateWorkflowFlowAutomation') {
      submitWorkflowFlowAutomationHttp({
        ...pendingTemplateMutation,
        template_change_mode: mode
      });
      handleCloseTemplateDecisionDialog();
      return;
    }

    if (pendingTemplateMutation.command === 'createWorkflowFlow') {
      submitWorkflowFlowCreateHttp({
        ...pendingTemplateMutation,
        template_change_mode: mode
      });
      handleCloseTemplateDecisionDialog();
      return;
    }

    sendTemplateAwareCommand({
      ...pendingTemplateMutation,
      template_change_mode: mode
    });
    handleCloseTemplateDecisionDialog();
  }, [
    pendingTemplateMutation,
    sendTemplateAwareCommand,
    submitWorkflowFlowCreateHttp,
    submitWorkflowFlowAutomationHttp,
    handleCloseTemplateDecisionDialog
  ]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleToggleFlowEnableResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;
    const success = responseData.success;

    if (success && responseData) {
      const { id, enable } = responseData;
      console.log(`Flow ${id} ${enable ? 'enabled' : 'disabled'} successfully`);

      // Update settings state
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === id ? { ...s, enable } : s
        )
      );

      // Update running workflows state
      setRunningWorkflows(prev => {
        const newSet = new Set(prev);
        if (enable === 1) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    } else {
      console.error(`Failed to toggle flow ${responseData?.id || 'unknown'}`, responseData);
      if (responseData?.id) {
        // Revert the optimistic update on failure
        setSettings(prevSettings =>
          prevSettings.map(s =>
            s.id === responseData.id ? { ...s, enable: responseData.enable === 1 ? 0 : 1 } : s
          )
        );

        // Revert running workflows state on failure
        setRunningWorkflows(prev => {
          const newSet = new Set(prev);
          const revertedEnable = responseData.enable === 1 ? 0 : 1;
          if (revertedEnable === 1) {
            newSet.add(responseData.id);
          } else {
            newSet.delete(responseData.id);
          }
          return newSet;
        });
      }
    }
  }, []);
  const handleGetAllUniqueTags = useCallback((parsedMessage) => {
    const receivedTags = parsedMessage.data.tags || [];
    console.log('Received tags:', receivedTags);
    setTags(receivedTags);
  }, []);

  const handleGetActionPlugins = useCallback((parsedMessage) => {
    const plugins = Array.isArray(parsedMessage.data) ? parsedMessage.data : [];
    setPluginActions(plugins.filter((plugin) => plugin && typeof plugin.type === 'string'));
    // Refresh list once plugin metadata is loaded to ensure plugin actions are not filtered out.
    if (caseid) {
      sendMessage({ command: 'getListExportsSettings', caseid });
    }
  }, [caseid, sendMessage]);

  const handlePluginImported = useCallback(() => {
    if (caseid) {
      sendMessage({ command: 'getActionPlugins', caseid });
    }
  }, [caseid, sendMessage]);

  const handleGetWorkflowFlowsResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    const flowList = Array.isArray(data.flows) ? data.flows : [];
    syncWorkflowFlows(flowList);
  }, [syncWorkflowFlows]);

  const handleCreateWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status === 'success' && data.flow?.id !== undefined) {
      if (Array.isArray(data.flows)) {
        syncWorkflowFlows(data.flows, data.flow.id);
      } else {
        setFlows((previousFlows) => {
          const withoutDuplicate = previousFlows.filter((flow) => String(flow.id) !== String(data.flow.id));
          return [...withoutDuplicate, data.flow];
        });
        setSelectedFlowId(data.flow.id);
      }
    } else if (data.status === 'error') {
      console.error(`Failed to create flow: ${data.message || 'Unknown error'}`);
    }
  }, [syncWorkflowFlows]);

  const handleRenameWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      console.error(`Failed to rename flow: ${data.message || 'Unknown error'}`);
      return;
    }
    const flowPayload = data.flow || {};
    setFlows((prev) => prev.map((flow) => (
      String(flow.id) === String(flowPayload.id)
        ? { ...flow, name: flowPayload.name || flow.name }
        : flow
    )));
  }, []);

  const handleDeleteWorkflowFlowResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      console.error(`Failed to delete flow: ${data.message || 'Unknown error'}`);
      return;
    }
    if (data.replacement_flow_id !== undefined && data.replacement_flow_id !== null) {
      setSelectedFlowId(data.replacement_flow_id);
    }
  }, []);

  const handleUpdateWorkflowFlowAutomationResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status !== 'success') {
      const errorMessage = data.message || 'Unknown error';
      console.error(`Failed to update flow automation: ${errorMessage}`);
      window.alert(errorMessage);
      loadWorkflowFlows();
      return;
    }

    const updatedFlow = data.flow || {};
    if (updatedFlow.id !== undefined && updatedFlow.id !== null) {
      setFlows((prev) => prev.map((flow) => (
        String(flow.id) === String(updatedFlow.id)
          ? { ...flow, ...updatedFlow }
          : flow
      )));
      return;
    }

    loadWorkflowFlows();
  }, [loadWorkflowFlows]);

  const handleWorkflowFlowOperationResponse = useCallback((parsedMessage) => {
    const data = parsedMessage?.data || {};
    if (data.status === 'error') {
      const errorMessage = data.message || 'Unknown error';
      console.error(`Workflow flow operation failed: ${errorMessage}`);
      window.alert(errorMessage);
    }
  }, []);

  const handleFlowRename = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;

    if (responseData && responseData.status === "success") {
      const { id, name } = responseData;
      console.log(`Flow ${id} renamed to '${name}' successfully`);
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === id ? { ...s, name } : s
        )
      );
      if (pendingCopyAction) {
        setPendingCopyAction(null);
      }
    } else {
      console.error(`Failed to rename flow: ${responseData?.message || 'Unknown error'}`);
      // Optionally show an error message to the user
    }
  }, [pendingCopyAction]);

  const handleToggleActionScopeResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data;

    // Clear loading state
    setTogglingScope(null);
    setScopeChangeDialogOpen(false);
    setPendingScopeChange(null);

    if (responseData && responseData.success) {
      // Update UI only on successful response from server
      setSettings(prevSettings =>
        prevSettings.map(s =>
          s.id === responseData.id ? { ...s, scope: responseData.scope } : s
        )
      );
      setSnackbar({
        open: true,
        message: responseData.scope === 'project'
          ? t(
            'workflow.scope_changed_to_project_warning',
            'Warning: this action now runs once at the project level, after events/files are processed.'
          )
          : t(
            'workflow.scope_changed_to_event_warning',
            'Warning: this action now runs for each individual event/file.'
          ),
        severity: 'warning'
      });
      console.log(`Action ${responseData.id} scope updated to ${responseData.scope}`);
    } else {
      console.error('Error toggling action scope:', responseData?.message);
      // Show error notification if needed
      alert(`Failed to toggle scope: ${responseData?.message || 'Unknown error'}`);
    }
  }, [t]);

  const getScopeChangeConfirmation = useCallback((nextScope) => ({
    title: nextScope === 'project'
      ? t('workflow.scope_change_confirm_project_title', 'Confirm project scope change')
      : t('workflow.scope_change_confirm_event_title', 'Confirm event scope change'),
    message: nextScope === 'project'
      ? t(
        'workflow.scope_change_confirm_project_message',
        'This action will stop running on each event/file and will instead run once when the project batch is processed.'
      )
      : t(
        'workflow.scope_change_confirm_event_message',
        'This action will start running on each individual event/file instead of once at the project level.'
      ),
    confirmLabel: nextScope === 'project'
      ? t('workflow.scope_change_confirm_project_action', 'Switch to project')
      : t('workflow.scope_change_confirm_event_action', 'Switch to event'),
  }), [t]);

  const requestScopeChange = useCallback((settingId, nextScope) => {
    if (!settingId || !nextScope) {
      return;
    }

    const confirmation = getScopeChangeConfirmation(nextScope);
    setPendingScopeChange({
      settingId,
      nextScope,
      ...confirmation
    });
    setScopeChangeDialogOpen(true);
  }, [getScopeChangeConfirmation]);

  const handleConfirmScopeChange = useCallback(() => {
    if (!pendingScopeChange) {
      setScopeChangeDialogOpen(false);
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for scope change");
      setScopeChangeDialogOpen(false);
      setPendingScopeChange(null);
      return;
    }

    setTogglingScope(pendingScopeChange.settingId);
    console.log(`Setting scope for action ${pendingScopeChange.settingId} to ${pendingScopeChange.nextScope}`);
    sendTemplateAwareCommand({
      command: 'toggleActionScope',
      id: pendingScopeChange.settingId,
      scope: pendingScopeChange.nextScope,
      caseid: caseid
    });
  }, [caseid, pendingScopeChange, sendTemplateAwareCommand, ws]);

  const handleCancelScopeChange = useCallback(() => {
    setScopeChangeDialogOpen(false);
    setPendingScopeChange(null);
  }, []);

  const handleStepProgress = useCallback((parsedMessage) => {
    console.log('handleStepProgress received:', parsedMessage);
    const progress = parsedMessage.data || {};
    console.log('Progress data:', progress);

    if (progress.status === 'processing') {
      console.log('Setting current processing step:', progress.step_id);
      setCurrentProcessingStep(progress.step_id);
      setStartedSteps(prev => new Set([...prev, progress.step_id]));
    } else if (progress.status === 'completed') {
      console.log('Adding to processed steps:', progress.step_id);
      setProcessedSteps(prev => new Set([...prev, progress.step_id]));
      // Don't clear currentProcessingStep here - let the next 'processing' step handle it
      // setCurrentProcessingStep(null);
    }
  }, []);

  const handleForceRun = useCallback((parsedMessage) => {
    // This handler prevents the "No handler found for command: forceRun" console message
    // Individual action components handle their own forceRun responses
    console.log('forceRun command acknowledged in ActionsDataflowList:', parsedMessage);
  }, []);

  const handleReplayAllWorkflowsResponse = useCallback((parsedMessage) => {
    const responseData = parsedMessage.data || {};

    // Handle step progress updates
    if (responseData.step_progress) {
      const { current_step, step_id, step_name, status, total_steps } = responseData.step_progress;

      if (status === 'processing') {
        setCurrentProcessingStep({ id: step_id, name: step_name, index: current_step });
      } else if (status === 'completed') {
        setProcessedSteps(prev => new Set([...prev, step_id]));
        setCurrentProcessingStep(null);
      } else if (status === 'failed') {
        // Handle failed step - mark as processed but keep error indication
        const errorMessage = responseData.step_progress.error || 'Unknown error';
        console.error(`Step ${step_id} (${step_name}) failed: ${errorMessage}`);
        setProcessedSteps(prev => new Set([...prev, step_id]));
        setCurrentProcessingStep(null);

        // Show error notification
        setWorkflowError({
          stepId: step_id,
          stepName: step_name,
          stepIndex: current_step,
          totalSteps: total_steps,
          errorMessage: errorMessage
        });
      }

      return; // Don't reset state for progress updates
    }

    // Clear timeout if it exists
    if (replayTimeoutId) {
      clearTimeout(replayTimeoutId);
      setReplayTimeoutId(null);
    }

    // Reset processing state
    setIsReplayingAllWorkflows(false);
    setCurrentProcessingStep(null);

    if (responseData && responseData.status === "success") {
      console.log(`Successfully replayed all workflows: ${responseData.message}`);
      // Clear the visual states after a delay to show completion
      setTimeout(() => {
        setProcessedSteps(new Set());
        setStartedSteps(new Set());
      }, 2000);
    } else if (responseData && responseData.status === "error") {
      // Handle explicit error response
      console.error(`Failed to replay all workflows: ${responseData?.message || 'Unknown error'}`);
      // Clear states immediately on error
      setProcessedSteps(new Set());
      setStartedSteps(new Set());

      // Show error notification
      setWorkflowError({
        stepId: responseData.stopped_at_workflow || 'unknown',
        stepName: 'Workflow Processing',
        stepIndex: responseData.workflows_count || 0,
        totalSteps: responseData.workflows_count || 0,
        errorMessage: responseData.message || responseData.error || 'Unknown error'
      });
    } else {
      // Handle unexpected response format or missing status
      console.error(`Unexpected response from replay all workflows:`, responseData);
      // Clear states immediately on unexpected response
      setProcessedSteps(new Set());
      setStartedSteps(new Set());
    }
  }, [replayTimeoutId]);

  // Optimized WebSocket message routing
  const messageHandlers = useMemo(() => ({
    'getActionPlugins': handleGetActionPlugins,
    'getWorkflowFlowsResponse': handleGetWorkflowFlowsResponse,
    'getListExportSettings': handleGetListExportSettings,
    'saveActionSettings': handleSaveActionSettings,
    'templateChangeDecisionRequired': handleTemplateChangeDecisionRequired,
    'templatePropagationJobStatus': handleTemplatePropagationJobStatus,
    'templateProjectDetached': handleTemplateDetachSuccess,
    'get_all_unique_tags': handleGetAllUniqueTags,
    'toggleFlowEnableResponse': handleToggleFlowEnableResponse,
    'toggleActionScope': handleToggleActionScopeResponse,
    'flowRename': handleFlowRename,
    'createWorkflowFlowResponse': handleCreateWorkflowFlowResponse,
    'updateActionExecutionResponse': handleWorkflowFlowOperationResponse,
    'updateActionTargetExecutionResponse': handleWorkflowFlowOperationResponse,
    'renameWorkflowFlowResponse': handleRenameWorkflowFlowResponse,
    'deleteWorkflowFlowResponse': handleDeleteWorkflowFlowResponse,
    'updateWorkflowFlowAutomationResponse': handleUpdateWorkflowFlowAutomationResponse,
    'swapWorkflowFlowOrderResponse': handleWorkflowFlowOperationResponse,
    'moveActionToFlowResponse': handleWorkflowFlowOperationResponse,
    'forceRun': handleForceRun,
    'replayAllWorkflows': handleReplayAllWorkflowsResponse,
    'stepProgress': handleStepProgress
  }), [
    handleGetActionPlugins,
    handleGetWorkflowFlowsResponse,
    handleGetListExportSettings,
    handleSaveActionSettings,
    handleTemplateChangeDecisionRequired,
    handleTemplatePropagationJobStatus,
    handleTemplateDetachSuccess,
    handleGetAllUniqueTags,
    handleToggleFlowEnableResponse,
    handleToggleActionScopeResponse,
    handleFlowRename,
    handleCreateWorkflowFlowResponse,
    handleRenameWorkflowFlowResponse,
    handleDeleteWorkflowFlowResponse,
    handleUpdateWorkflowFlowAutomationResponse,
    handleWorkflowFlowOperationResponse,
    handleForceRun,
    handleReplayAllWorkflowsResponse,
    handleStepProgress
  ]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        console.log('WebSocket message received in ActionsDataflowList:', parsedMessage);
        const handler = messageHandlers[parsedMessage.command];
        if (handler) {
          console.log('Found handler for command:', parsedMessage.command);
          handler(parsedMessage);
        } else {
          console.log('No handler found for command:', parsedMessage.command);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message in ActionsDataflowList:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, messageHandlers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (replayTimeoutId) {
        clearTimeout(replayTimeoutId);
      }
    };
  }, [replayTimeoutId]);

  const handleSettingSaveStatusChange = useCallback((itemId, isDirty, nameOrSettingObject, oldItemIdIfChanged) => {
    setSettings(prevSettings => {
      // Check if this is a new setting (temp ID that doesn't exist in the list)
      const existingSettingIndex = prevSettings.findIndex(s => s.id === (oldItemIdIfChanged || itemId));

      if (existingSettingIndex === -1) {
        // This is a new setting - add it to the list
        if (typeof nameOrSettingObject === 'object' && nameOrSettingObject !== null) {
          const newSetting = {
            id: itemId,
            type: selectedSettingType,
            enable: 0,
            caseid: caseid,
            interval: '0',
            name: nameOrSettingObject.name || pluginNameByType[selectedSettingType] || t(`settings.new_${selectedSettingType}_name`, `New ${selectedSettingType.charAt(0).toUpperCase() + selectedSettingType.slice(1)}`),
            flow_id: selectedFlowId ?? (flows[0]?.id ?? null),
            data: nameOrSettingObject.data || {},
            ...nameOrSettingObject
          };
          return [...prevSettings, newSetting];
        }
        return prevSettings;
      }

      // Existing setting - update it
      return prevSettings.map(s => {
        if (s.id === (oldItemIdIfChanged || itemId)) {
          const currentLocalSetting = s;
          let newNameString = currentLocalSetting.name;
          let updatesFromObject = {};

          if (typeof nameOrSettingObject === 'string') {
            newNameString = nameOrSettingObject;
          } else if (typeof nameOrSettingObject === 'object' && nameOrSettingObject !== null) {
            updatesFromObject = { ...nameOrSettingObject };
            if (nameOrSettingObject.name !== undefined && typeof nameOrSettingObject.name === 'string') {
              newNameString = nameOrSettingObject.name;
            } else if (nameOrSettingObject.name !== undefined && typeof nameOrSettingObject.name !== 'string') {
              console.warn('Setting item received an object with a non-string name property:', nameOrSettingObject);
            }
          }

          const updatedSetting = {
            ...currentLocalSetting,
            ...updatesFromObject,
            id: itemId,
            name: newNameString
          };

          return updatedSetting;
        }
        return s;
      });
    });

    if (oldItemIdIfChanged) {
      setSelectedSettingId(itemId); // Update selection to the new permanent ID
    }

    // If the item was saved (not dirty) and the dialog is open for this item, close it.
    // Only close if a temporary item was saved and became permanent (oldItemIdIfChanged is true).
    // For existing items, saving them will make them !isDirty, but the dialog will remain open.
    if (!isDirty && isSettingDialogOpen && (selectedSettingId === itemId || selectedSettingId === oldItemIdIfChanged)) {
      if (oldItemIdIfChanged) {
        setIsSettingDialogOpen(false);
      }
    }
  }, [selectedSettingType, caseid, t, selectedSettingId, isSettingDialogOpen, pluginNameByType, selectedFlowId, flows]);
  // Event handlers with useCallback optimization
  const handleToggleEnable = useCallback((settingId, currentEnableState) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for toggle enable/disable");
      return;
    }

    const newEnableState = currentEnableState === 1 ? 0 : 1;

    console.log(`Toggling flow ${settingId} from ${currentEnableState} to ${newEnableState}`);
    sendTemplateAwareCommand({
      command: 'toggleFlowEnable',
      id: settingId,
      enable: newEnableState,
      caseid: caseid
    });

    // Optimistic update
    setSettings(prevSettings => {
      const updatedSettings = prevSettings.map(s =>
        s.id === settingId ? { ...s, enable: newEnableState } : s
      );
      return updatedSettings;
    });

    // Track running workflows
    setRunningWorkflows(prev => {
      const newSet = new Set(prev);
      if (newEnableState === 1) {
        newSet.add(settingId);
      } else {
        newSet.delete(settingId);
      }
      return newSet;
    });
  }, [ws, caseid]);

  const handleToggleScope = useCallback((settingId, currentScope) => {
    const nextScope = currentScope === 'project' ? 'event' : 'project';
    requestScopeChange(settingId, nextScope);
  }, [requestScopeChange]);

  // Handler for direct scope change (used in dialog)
  const handleScopeChange = useCallback((settingId, newScope) => {
    // For new/temp settings, update local state only
    const isNewSetting = isTemporarySettingId(settingId);

    if (isNewSetting) {
      setNewActionDraft((prevDraft) => (
        prevDraft && prevDraft.id === settingId
          ? { ...prevDraft, scope: newScope }
          : prevDraft
      ));
      console.log(`Updated local scope for new action ${settingId} to ${newScope}`);
      return;
    }

    // For existing settings, ask for confirmation before sending to the server
    requestScopeChange(settingId, newScope);
  }, [requestScopeChange]);

  // Menu handlers for AddActionMenu component
  const handleOpenMenu = useCallback(() => setAnchorEl(true), []);
  const handleCloseMenu = useCallback(() => setAnchorEl(false), []);
  const handleAddNewSetting = useCallback((type, scope = 'event') => {
    const targetFlowId = selectedFlowId ?? (sortedFlows[0]?.id ?? null);

    // We allow adding new settings even if others are unsaved/disabled
    // The backend will handle the persistence immediately

    const tempId = `temp_${Date.now()}`;
    // Don't add to settings list immediately - just set up for dialog
    setSelectedSettingId(tempId);
    setSelectedSettingType(type);

    setNewActionDraft({
      id: tempId,
      type: type,
      enable: 0,
      caseid: caseid,
      interval: '0',
      name: '',
      scope: scope,
      flow_id: targetFlowId,
      target_execution_mode: 'sequential',
      max_parallel_targets: 5,
      data: {}
    });

    setIsSettingDialogOpen(true);

    // Immediately persist the new action to the backend
    if (ws && ws.readyState === WebSocket.OPEN) {
      sendTemplateAwareCommand({
        command: 'saveActionSettings',
        id: 0, // 0 indicates new record to backend
        caseid: caseid,
        type: type,
        enable: '0', // Disabled by default
        interval: 0,
        data: {},
        scope: scope,
        flow_id: targetFlowId,
        target_execution_mode: 'sequential',
        max_parallel_targets: 5
      });
    }
  }, [caseid, ws, selectedFlowId, sortedFlows]);

  const handleMenuItemSelect = useCallback((actionType, scope = 'event') => {
    handleCloseMenu();
    handleAddNewSetting(actionType, scope);
  }, [handleCloseMenu, handleAddNewSetting]);

  const handleDeleteConfirm = useCallback((settingId) => {
    setIdToDelete(settingId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setIdToDelete(null);
  }, []);

  const handleCopyAction = useCallback((sourceSetting) => {
    if (!sourceSetting || isTemporarySettingId(sourceSetting.id)) {
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not available for copying action');
      return;
    }

    const sourceFlowId = resolveSettingFlowId(sourceSetting);
    const sourceName = getSettingName(sourceSetting, nameMap, pluginNameByType);
    const copiedCustomData = (() => {
      const rawData = sourceSetting.customData ?? sourceSetting.data ?? {};
      try {
        return JSON.parse(JSON.stringify(rawData));
      } catch (_error) {
        return Array.isArray(rawData) ? [...rawData] : { ...(rawData || {}) };
      }
    })();

    setPendingCopyAction({
      sourceId: sourceSetting.id,
      sourceType: sourceSetting.type,
      sourceName,
    });

    sendTemplateAwareCommand({
      command: 'saveActionSettings',
      id: 0,
      caseid,
      type: sourceSetting.type,
      enable: isActionEnabled(sourceSetting.enable) ? '1' : '0',
      interval: sourceSetting.interval ?? 0,
      data: copiedCustomData,
      scope: sourceSetting.scope || 'event',
      flow_id: sourceFlowId,
      execution_group: sourceSetting.execution_group ?? null,
      execution_mode: sourceSetting.execution_mode ?? 'sequential',
      target_execution_mode: sourceSetting.target_execution_mode ?? 'sequential',
      max_parallel_targets: sourceSetting.max_parallel_targets ?? 5
    });
  }, [ws, caseid, nameMap, pluginNameByType, resolveSettingFlowId, sendTemplateAwareCommand]);

  const handleDeleteExecute = useCallback(() => {
    if (!idToDelete) return;
    const settingToDelete = settings.find(setting => setting.id === idToDelete);
    if (!settingToDelete) return;

    if (ws && ws.readyState === WebSocket.OPEN) {
      sendTemplateAwareCommand({
        command: 'deleteExportSettings',
        id: idToDelete,
        type: settingToDelete.type,
        caseid
      });
    }

    const updatedSettings = settings.filter(setting => setting.id !== idToDelete);
    setSettings(updatedSettings);

    if (selectedSettingId === idToDelete) {
      if (updatedSettings.length > 0) {
        setSelectedSettingId(updatedSettings[0].id);
        setSelectedSettingType(updatedSettings[0].type);
      } else {
        setSelectedSettingId(null);
        setSelectedSettingType(null);
      }
    }
    setDeleteConfirmOpen(false);
    setIdToDelete(null);
  }, [idToDelete, settings, ws, caseid, selectedSettingId]);

  const handleNameSave = useCallback((settingId, newName) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for name save");
      return;
    }

    const setting = settings.find(s => s.id === settingId);
    if (!setting) return;

    sendTemplateAwareCommand({
      command: 'flowRename',
      id: settingId,
      name: newName
    });

    setSettings(prevSettings =>
      prevSettings.map(s =>
        s.id === settingId ? { ...s, name: newName } : s
      )
    );
  }, [ws, settings]);

  const handleSettingDialogClose = useCallback(() => {
    // Just close the dialog and reset selection - no need to remove from list since we never added it
    setIsSettingDialogOpen(false);
    setSelectedSettingId(null);
    setSelectedSettingType(null);
    setNewActionDraft(null);
  }, []);

  const handleMoveSetting = useCallback((settingId, direction) => {
    setSettings(currentSettings => {
      const index = currentSettings.findIndex(s => s.id === settingId);
      if (index === -1) return currentSettings;

      const newSettings = [...currentSettings];
      const settingName = newSettings[index].name || newSettings[index].type;
      const currentFlowId = resolveSettingFlowId(newSettings[index]);

      let id1 = settingId;
      let id2;

      if (direction === 'left' && index > 0 && String(resolveSettingFlowId(newSettings[index - 1])) === String(currentFlowId)) {
        id2 = newSettings[index - 1].id;
        [newSettings[index - 1], newSettings[index]] = [newSettings[index], newSettings[index - 1]];
        console.log(`Moved setting '${settingName}' (ID: ${settingId}) LEFT. New order:`, newSettings.map(s => ({ id: s.id, name: s.name || s.type })));
        if (ws && ws.readyState === WebSocket.OPEN) {
          sendTemplateAwareCommand({
            command: 'swapfloworder',
            id1: id1,
            id2: id2,
            caseid: caseid
          });
        }
        return newSettings;
      } else if (direction === 'right' && index < newSettings.length - 1 && String(resolveSettingFlowId(newSettings[index + 1])) === String(currentFlowId)) {
        id2 = newSettings[index + 1].id;
        [newSettings[index + 1], newSettings[index]] = [newSettings[index], newSettings[index + 1]];
        console.log(`Moved setting '${settingName}' (ID: ${settingId}) RIGHT. New order:`, newSettings.map(s => ({ id: s.id, name: s.name || s.type })));
        if (ws && ws.readyState === WebSocket.OPEN) {
          sendTemplateAwareCommand({
            command: 'swapfloworder',
            id1: id1,
            id2: id2,
            caseid: caseid
          });
        }
        return newSettings;
      }
      return currentSettings;
    });
  }, [ws, caseid, resolveSettingFlowId, sendTemplateAwareCommand]);

  const handleMoveActionToFlow = useCallback((settingId, targetFlowId) => {
    const parsedTargetFlowId = parsePositiveInteger(targetFlowId);
    if (!parsedTargetFlowId) {
      console.warn('Invalid target flow ID for action move');
      return;
    }

    const targetFlowExists = sortedFlows.some((flow) => String(flow.id) === String(parsedTargetFlowId));
    if (!targetFlowExists) {
      console.warn(`Target flow ${parsedTargetFlowId} not found`);
      return;
    }

    const setting = settings.find((item) => item.id === settingId);
    if (!setting) {
      console.warn(`Could not find action ${settingId} to move`);
      return;
    }

    const sourceFlowId = resolveSettingFlowId(setting);
    if (String(sourceFlowId) === String(parsedTargetFlowId)) {
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for moving action to another flow");
      return;
    }

    sendTemplateAwareCommand({
      command: 'moveActionToFlow',
      action_id: settingId,
      flow_id: parsedTargetFlowId,
      caseid: caseid
    });
  }, [settings, sortedFlows, ws, caseid, resolveSettingFlowId, sendTemplateAwareCommand]);

  const updateActionExecution = useCallback((actionId, executionGroup, executionMode) => {
    const mode = normalizeActionExecutionMode(executionMode);
    const group = normalizeActionExecutionGroup(executionGroup, mode);
    setSettings((currentSettings) => currentSettings.map((setting) => (
      String(setting.id) === String(actionId)
        ? { ...setting, execution_group: group, execution_mode: mode }
        : setting
    )));
    if (ws && ws.readyState === WebSocket.OPEN) {
      sendTemplateAwareCommand({
        command: 'updateActionExecution',
        action_id: actionId,
        execution_group: group,
        execution_mode: mode,
        caseid
      });
    }
  }, [ws, caseid, sendTemplateAwareCommand]);

  const handleGroupWithPrevious = useCallback((settingId) => {
    const currentIndex = selectedFlowSettings.findIndex((setting) => String(setting.id) === String(settingId));
    if (currentIndex <= 0) {
      return;
    }
    const currentSetting = selectedFlowSettings[currentIndex];
    const previousSetting = selectedFlowSettings[currentIndex - 1];
    const group = normalizeActionExecutionGroup(previousSetting.execution_group, previousSetting.execution_mode)
      || `parallel-${Date.now()}-${previousSetting.id}`;

    updateActionExecution(previousSetting.id, group, 'parallel');
    updateActionExecution(currentSetting.id, group, 'parallel');
  }, [selectedFlowSettings, updateActionExecution]);

  const handleUngroupAction = useCallback((settingId) => {
    const targetSetting = selectedFlowSettings.find((setting) => String(setting.id) === String(settingId));
    if (!targetSetting) {
      return;
    }

    const group = normalizeActionExecutionGroup(targetSetting.execution_group, targetSetting.execution_mode);
    if (!group) {
      updateActionExecution(settingId, null, 'sequential');
      return;
    }

    const groupMembers = selectedFlowSettings.filter(
      (setting) => String(setting.execution_group || '') === group
    );
    const remainingMembers = groupMembers.filter((setting) => String(setting.id) !== String(settingId));

    updateActionExecution(settingId, null, 'sequential');

    if (remainingMembers.length < 2) {
      remainingMembers.forEach((setting) => {
        updateActionExecution(setting.id, null, 'sequential');
      });
    }
  }, [selectedFlowSettings, updateActionExecution]);

  const handleToggleExecutionGroupMode = useCallback((executionGroup, nextMode) => {
    const group = String(executionGroup || '').trim();
    if (!group) {
      return;
    }
    const mode = normalizeActionExecutionMode(nextMode);
    selectedFlowSettings
      .filter((setting) => String(setting.execution_group || '') === group)
      .forEach((setting) => updateActionExecution(setting.id, mode === 'parallel' ? group : null, mode));
  }, [selectedFlowSettings, updateActionExecution]);

  const handleActionTargetExecutionChange = useCallback((settingId, nextMode, nextMaxParallelTargets) => {
    const mode = normalizeActionTargetExecutionMode(nextMode);
    const maxParallel = normalizeMaxParallelTargets(nextMaxParallelTargets);

    setSettings((currentSettings) => currentSettings.map((setting) => (
      String(setting.id) === String(settingId)
        ? {
          ...setting,
          target_execution_mode: mode,
          max_parallel_targets: maxParallel,
        }
        : setting
    )));

    const isNewSetting = isTemporarySettingId(settingId);
    if (isNewSetting) {
      setNewActionDraft((prevDraft) => (
        prevDraft && prevDraft.id === settingId
          ? {
            ...prevDraft,
            target_execution_mode: mode,
            max_parallel_targets: maxParallel,
          }
          : prevDraft
      ));
      return;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for target execution change");
      return;
    }

    sendTemplateAwareCommand({
      command: 'updateActionTargetExecution',
      action_id: settingId,
      target_execution_mode: mode,
      max_parallel_targets: maxParallel,
      caseid
    });
  }, [ws, caseid, sendTemplateAwareCommand]);

  const handleReplayConfirmOpen = useCallback(() => {
    if (isFlowRunDisabled) return;
    setReplayConfirmOpen(true);
  }, [isFlowRunDisabled]);

  const handleReplayConfirmClose = useCallback(() => {
    setReplayConfirmOpen(false);
  }, []);

  const handleReplayAllWorkflows = useCallback(() => {
    setReplayConfirmOpen(false); // Close dialog

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not available for replay all workflows");
      return;
    }

    if (!caseid) {
      console.warn("No case ID available for replay all workflows");
      return;
    }

    if (isReplayingAllWorkflows) {
      console.warn("Replay all workflows is already in progress");
      return;
    }

    console.log(`Triggering replay of all workflows for case ${caseid}`);

    // Set processing state and reset step tracking
    setIsReplayingAllWorkflows(true);
    setCurrentProcessingStep(null);
    setProcessedSteps(new Set());
    setStartedSteps(new Set());

    // Set a timeout to reset the state if no response is received (30 seconds)
    const timeoutId = setTimeout(() => {
      console.warn("Replay all workflows timed out - resetting state");
      setIsReplayingAllWorkflows(false);
      setCurrentProcessingStep(null);
      setProcessedSteps(new Set());
      setStartedSteps(new Set());
      setReplayTimeoutId(null);
    }, 30000);

    setReplayTimeoutId(timeoutId);

    // Send command to trigger all workflows for this case
    ws.send(JSON.stringify({
      command: 'replayAllWorkflows',
      caseid: caseid
    }));

    // Optionally show a notification or update UI state to indicate the action was triggered
    // You could add a toast notification here if you have a notification system

  }, [ws, caseid, isReplayingAllWorkflows]);

  const openFlowAutomationDialog = useCallback((mode, flow = null) => {
    const normalizedMode = mode === 'edit' ? 'edit' : 'create';
    const isEdit = normalizedMode === 'edit';
    const triggerMode = isEdit
      ? normalizeFlowTriggerMode(flow?.trigger_mode, flow?.auto_run_enabled)
      : 'event';
    const frequencyValue = isEdit && triggerMode === 'time'
      ? Math.max(1, Number(flow?.auto_run_frequency_value || 1))
      : 1;
    const frequencyUnit = normalizeFlowAutomationUnit(
      isEdit ? flow?.auto_run_frequency_unit : 'minutes'
    );
    const scheduleDayOfWeek = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleDayOfWeek(flow?.auto_run_schedule_day_of_week)
      : [];
    const scheduleDayOfMonth = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleDayOfMonth(flow?.auto_run_schedule_day_of_month)
      : [];
    const scheduleMonth = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleMonth(flow?.auto_run_schedule_month)
      : [];
    const scheduleHour = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleHour(flow?.auto_run_schedule_hour)
      : [];
    const scheduleMinute = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleMinute(flow?.auto_run_schedule_minute)
      : [];
    const scheduleTimezone = isEdit && triggerMode === 'calendar'
      ? normalizeFlowScheduleTimezone(flow?.auto_run_schedule_timezone || browserTimezone)
      : browserTimezone;
    const triggerFilter = isEdit && triggerMode === 'event_change'
      ? normalizeFlowTriggerFilter(flow?.trigger_filter)
      : DEFAULT_FLOW_TRIGGER_FILTER;

    setFlowAutomationDialogMode(normalizedMode);
    setFlowAutomationDialogFlowId(isEdit ? flow?.id ?? null : null);
    setFlowAutomationDialogName(
      isEdit
        ? String(flow?.name || '')
        : t('workflow.automation_mode_event', 'New or updated event')
    );
    setFlowAutomationDialogTriggerMode(triggerMode);
    setFlowAutomationDialogTriggerFilter(triggerFilter);
    setFlowAutomationDialogFrequencyValue(String(frequencyValue));
    setFlowAutomationDialogFrequencyUnit(frequencyUnit);
    setFlowAutomationDialogScheduleDayOfWeek(scheduleDayOfWeek);
    setFlowAutomationDialogScheduleDayOfMonth(scheduleDayOfMonth);
    setFlowAutomationDialogScheduleMonth(scheduleMonth);
    setFlowAutomationDialogScheduleHour(scheduleHour);
    setFlowAutomationDialogScheduleMinute(scheduleMinute);
    setFlowAutomationDialogScheduleTimezone(scheduleTimezone);
    setFlowAutomationDialogError('');
    setFlowAutomationDialogOpen(true);
  }, [t, browserTimezone]);

  const handleCloseFlowAutomationDialog = useCallback(() => {
    setFlowAutomationDialogOpen(false);
    setFlowAutomationDialogError('');
    setFlowAutomationDialogSubmitting(false);
  }, []);

  const handleSubmitFlowAutomationDialog = useCallback(async () => {
    if (!caseid) {
      return;
    }

    const triggerMode = normalizeFlowTriggerMode(flowAutomationDialogTriggerMode);
    let frequencyValue = 1;
    if (triggerMode === 'time') {
      frequencyValue = Number(flowAutomationDialogFrequencyValue);
      if (!Number.isInteger(frequencyValue) || frequencyValue <= 0) {
        setFlowAutomationDialogError(
          t('workflow.automation_frequency_value_invalid', 'Frequency must be a number greater than 0.')
        );
        return;
      }
    }

    const frequencyUnit = normalizeFlowAutomationUnit(flowAutomationDialogFrequencyUnit);
    const automationPayload = buildFlowAutomationPayload(
      triggerMode,
      frequencyValue,
      frequencyUnit,
      flowAutomationDialogScheduleDayOfWeek,
      flowAutomationDialogScheduleDayOfMonth,
      flowAutomationDialogScheduleMonth,
      flowAutomationDialogScheduleHour,
      flowAutomationDialogScheduleMinute,
      flowAutomationDialogScheduleTimezone,
      flowAutomationDialogTriggerFilter
    );

    if (flowAutomationDialogMode === 'create') {
      const flowName = String(flowAutomationDialogName || '').trim();
      if (!flowName) {
        setFlowAutomationDialogError(
          t('workflow.automation_flow_name_required', 'Flow name is required.')
        );
        return;
      }
      setFlowAutomationDialogSubmitting(true);
      const createResult = await submitWorkflowFlowCreateHttp({
        command: 'createWorkflowFlow',
        caseid,
        name: flowName,
        ...automationPayload
      });
      if (!createResult) {
        setFlowAutomationDialogSubmitting(false);
        setFlowAutomationDialogError(
          t('workflow.looptriggerflow.create_flow_error', 'Could not create the flow. Try again.')
        );
        return;
      }
      if (createResult === 'decision_required') {
        setFlowAutomationDialogSubmitting(false);
        handleCloseFlowAutomationDialog();
        return;
      }
      setFlowAutomationDialogSubmitting(false);
    } else {
      if (!flowAutomationDialogFlowId) {
        setFlowAutomationDialogError(t('workflow.invalid_flow_id', 'Invalid flow ID.'));
        return;
      }
      setFlowAutomationDialogSubmitting(true);
      const didSubmit = await submitWorkflowFlowAutomationHttp({
        command: 'updateWorkflowFlowAutomation',
        caseid,
        flow_id: flowAutomationDialogFlowId,
        ...automationPayload
      });
      if (!didSubmit) {
        setFlowAutomationDialogSubmitting(false);
        return;
      }
      if (didSubmit === 'decision_required') {
        setFlowAutomationDialogSubmitting(false);
        handleCloseFlowAutomationDialog();
        return;
      }
    }

    handleCloseFlowAutomationDialog();
  }, [
    caseid,
    flowAutomationDialogTriggerMode,
    flowAutomationDialogFrequencyValue,
    flowAutomationDialogFrequencyUnit,
    flowAutomationDialogScheduleDayOfWeek,
    flowAutomationDialogScheduleDayOfMonth,
    flowAutomationDialogScheduleMonth,
    flowAutomationDialogScheduleHour,
    flowAutomationDialogScheduleMinute,
    flowAutomationDialogScheduleTimezone,
    flowAutomationDialogTriggerFilter,
    flowAutomationDialogMode,
    flowAutomationDialogName,
    flowAutomationDialogFlowId,
    t,
    handleCloseFlowAutomationDialog,
    submitWorkflowFlowCreateHttp,
    submitWorkflowFlowAutomationHttp
  ]);

  const handleCreateFlow = useCallback(() => {
    if (!caseid) {
      return;
    }
    openFlowAutomationDialog('create');
  }, [caseid, openFlowAutomationDialog]);

  const handleCreateWorkflowFlowFromAction = useCallback((payload = {}) => {
    const flowName = String(payload.name || '').trim();
    if (!flowName) {
      return false;
    }

    return submitWorkflowFlowCreateHttp({
      command: 'createWorkflowFlow',
      caseid,
      name: flowName,
      auto_run_enabled: false,
      trigger_mode: 'disabled',
      auto_run_frequency_value: 0,
      auto_run_frequency_unit: 'minutes',
      ...payload
    });
  }, [caseid, submitWorkflowFlowCreateHttp]);

  const handleRenameSelectedFlow = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    setFlowRenameDialogValue(String(selectedFlow.name || ''));
    setFlowRenameDialogError('');
    setFlowRenameDialogOpen(true);
  }, [ws, selectedFlow]);

  const handleCloseFlowRenameDialog = useCallback(() => {
    setFlowRenameDialogOpen(false);
    setFlowRenameDialogError('');
  }, []);

  const handleSubmitFlowRenameDialog = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    const proposedName = String(flowRenameDialogValue || '').trim();
    if (!proposedName) {
      setFlowRenameDialogError(
        t('workflow.automation_flow_name_required', 'Flow name is required.')
      );
      return;
    }
    sendTemplateAwareCommand({
      command: 'renameWorkflowFlow',
      flow_id: selectedFlow.id,
      name: proposedName
    });
    handleCloseFlowRenameDialog();
  }, [ws, selectedFlow, flowRenameDialogValue, t, handleCloseFlowRenameDialog, sendTemplateAwareCommand]);

  const handleDeleteSelectedFlow = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow) {
      return;
    }
    if (isSelectedFlowMain) {
      return;
    }
    setFlowDeleteDialogOpen(true);
  }, [ws, selectedFlow, isSelectedFlowMain, sendTemplateAwareCommand]);

  const handleCloseFlowDeleteDialog = useCallback(() => {
    setFlowDeleteDialogOpen(false);
  }, []);

  const handleSubmitFlowDeleteDialog = useCallback(() => {
    if (!ws || ws.readyState !== WebSocket.OPEN || !selectedFlow || isSelectedFlowMain) {
      return;
    }
    sendTemplateAwareCommand({
      command: 'deleteWorkflowFlow',
      flow_id: selectedFlow.id
    });
    setFlowDeleteDialogOpen(false);
  }, [ws, selectedFlow, isSelectedFlowMain]);

  const handleConfigureSelectedFlowAutomation = useCallback(() => {
    if (!selectedFlow) {
      return;
    }
    if (isSelectedFlowMain) {
      window.alert(t('workflow.main_flow_automation_forbidden', 'Main Flow automation is disabled.'));
      return;
    }
    openFlowAutomationDialog('edit', selectedFlow);
  }, [selectedFlow, isSelectedFlowMain, t, openFlowAutomationDialog]);

  const handleTriggerNodeClick = useCallback(() => {
    if (!selectedFlow || isSelectedFlowMain) {
      return;
    }
    handleConfigureSelectedFlowAutomation();
  }, [selectedFlow, isSelectedFlowMain, handleConfigureSelectedFlowAutomation]);

  const handleCanvasPointerDown = useCallback((event) => {
    if (event.button !== 0 || isInteractiveCanvasTarget(event.target)) {
      return;
    }
    if (event.target?.closest?.('[data-workflow-node="true"]')) {
      return;
    }
    canvasInteractionRef.current = {
      type: 'pan',
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startViewport: canvasViewport
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [canvasViewport]);

  const handleCanvasNodePointerDown = useCallback((nodeId, event) => {
    if (event.button !== 0 || isInteractiveCanvasTarget(event.target)) {
      return;
    }
    event.stopPropagation();
    const startPosition = getCanvasNodePosition(nodeId);
    canvasInteractionRef.current = {
      type: 'node',
      nodeId,
      flowKey: canvasFlowKey,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPosition,
      startScale: canvasViewport.scale || 1,
      moved: false
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [canvasFlowKey, canvasViewport.scale, getCanvasNodePosition]);

  const handleCanvasNodePointerUp = useCallback((nodeId, event, onActivate) => {
    const interaction = canvasInteractionRef.current;
    if (interaction?.type !== 'node' || interaction.nodeId !== nodeId || interaction.moved) {
      return;
    }

    event.stopPropagation();
    canvasInteractionRef.current = null;
    onActivate(event);
  }, []);

  const persistWorkflowCanvasLayout = useCallback((flowKey, nodePositions) => {
    const flowId = Number(flowKey);
    if (!caseid || !Number.isInteger(flowId) || flowId <= 0) {
      return Promise.resolve();
    }
    const persistablePositions = getPersistableWorkflowCanvasNodePositions(nodePositions);
    return updateProjectWorkflowFlowLayout(caseid, flowId, persistablePositions).catch((error) => {
      console.error(`Failed to save workflow canvas layout for flow ${flowId}:`, error);
    });
  }, [caseid]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      const interaction = canvasInteractionRef.current;
      if (!interaction) {
        return;
      }

      const deltaX = event.clientX - interaction.startClientX;
      const deltaY = event.clientY - interaction.startClientY;

      if (interaction.type === 'pan') {
        setCanvasViewport({
          ...interaction.startViewport,
          x: interaction.startViewport.x + deltaX,
          y: interaction.startViewport.y + deltaY
        });
        return;
      }

      if (interaction.type === 'node') {
        const scale = interaction.startScale || 1;
        const moved = Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4;
        if (moved) {
          interaction.moved = true;
        }
        const nextPosition = {
          x: interaction.startPosition.x + deltaX / scale,
          y: interaction.startPosition.y + deltaY / scale
        };
        interaction.latestPosition = nextPosition;
        setCanvasNodePositions((previous) => {
          const nextPositions = {
            ...previous,
            [interaction.flowKey]: {
              ...(previous[interaction.flowKey] || {}),
              [interaction.nodeId]: nextPosition
            }
          };
          canvasNodePositionsRef.current = nextPositions;
          return nextPositions;
        });
      }
    };

    const handlePointerUp = () => {
      const interaction = canvasInteractionRef.current;
      if (interaction?.type === 'node' && interaction.moved) {
        const flowPositions = {
          ...(canvasNodePositionsRef.current[interaction.flowKey] || {}),
          ...(interaction.latestPosition ? { [interaction.nodeId]: interaction.latestPosition } : {})
        };
        persistWorkflowCanvasLayout(interaction.flowKey, flowPositions);
        ignoredNodeClickRef.current = interaction.nodeId;
        window.setTimeout(() => {
          if (ignoredNodeClickRef.current === interaction.nodeId) {
            ignoredNodeClickRef.current = null;
          }
        }, 0);
      }
      canvasInteractionRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [persistWorkflowCanvasLayout]);

  const handleCanvasWheel = useCallback((event) => {
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    const nextScale = Math.min(1.6, Math.max(0.55, canvasViewport.scale + (event.deltaY < 0 ? 0.08 : -0.08)));
    const scaleRatio = nextScale / canvasViewport.scale;
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;

    setCanvasViewport({
      scale: nextScale,
      x: pointerX - (pointerX - canvasViewport.x) * scaleRatio,
      y: pointerY - (pointerY - canvasViewport.y) * scaleRatio
    });
  }, [canvasViewport]);

  const handleResetCanvas = useCallback(() => {
    setCanvasViewport({ ...WORKFLOW_CANVAS_DEFAULT_VIEWPORT });
    setCanvasNodePositions((previous) => {
      const nextPositions = {
        ...previous,
        [canvasFlowKey]: {}
      };
      canvasNodePositionsRef.current = nextPositions;
      return nextPositions;
    });
    persistWorkflowCanvasLayout(canvasFlowKey, {});
  }, [canvasFlowKey, persistWorkflowCanvasLayout]);

  const handleConfirmCanvasReset = useCallback(() => {
    setCanvasResetConfirmOpen(false);
    handleResetCanvas();
  }, [handleResetCanvas]);

  const handleZoomCanvas = useCallback((direction) => {
    setCanvasViewport((previous) => ({
      ...previous,
      scale: Math.min(1.6, Math.max(0.55, previous.scale + direction * 0.1))
    }));
  }, []);

  // Memoized style objects for performance
  const styles = useMemo(() => ({
    controlButton: {
      color: '#5757b0',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'transparent',
      opacity: 1, // Keep control buttons fully opaque even when the action is disabled
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(87, 87, 176, 0.1)',
        color: '#4a4a9a',
        transform: 'scale(1.1)',
      },
      fontSize: '0.9rem',
      fontWeight: 'bold'
    },
    deleteButton: {
      color: '#ff4444',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'transparent',
      opacity: 1, // Keep delete button fully opaque when the action is disabled
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: 'rgba(255, 68, 68, 0.1)',
        color: '#cc0000',
        transform: 'scale(1.1)',
      }
    }
  }), []);

  // Get the visual status of each workflow item
  const getItemStatus = useCallback((itemId) => {
    if (processedSteps.has(itemId)) {
      return 'finished';
    } else if (currentProcessingStep === itemId || startedSteps.has(itemId)) {
      return 'started';
    } else {
      return 'pending';
    }
  }, [processedSteps, currentProcessingStep, startedSteps]);

  const renderWorkflowCanvasNode = (node) => {
    const position = getCanvasNodePosition(node.id);
    const nodeCommonSx = {
      position: 'absolute',
      left: position.x,
      top: position.y,
      width: WORKFLOW_CANVAS_NODE_WIDTH,
      minHeight: WORKFLOW_CANVAS_NODE_HEIGHT,
      display: 'flex',
      justifyContent: 'center',
      cursor: canvasInteractionRef.current?.type === 'node' ? 'grabbing' : 'grab',
      userSelect: 'none'
    };

    if (node.kind === 'trigger') {
      return (
        <Box
          key={node.id}
          data-workflow-node="true"
          onPointerDown={(event) => handleCanvasNodePointerDown(node.id, event)}
          onPointerUp={(event) => handleCanvasNodePointerUp(node.id, event, handleTriggerNodeClick)}
          sx={nodeCommonSx}
        >
          <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
            <AppTooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {t('workflow.automation_trigger_node_label', 'Flow Trigger')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    {isSelectedFlowMain
                      ? t('workflow.automation_trigger_on_event', 'New or updated event')
                      : selectedFlowTriggerModeLabel || t('workflow.automation_trigger_click_config', 'Click to configure automation')}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <Box
                onClick={(event) => {
                  if (ignoredNodeClickRef.current === node.id) {
                    ignoredNodeClickRef.current = null;
                    return;
                  }
                  event.stopPropagation();
                  handleTriggerNodeClick();
                }}
                sx={{
                  color: selectedFlowTriggerVisual.iconColor,
                  border: `3px solid ${selectedFlowTriggerVisual.borderColor}`,
                  background: selectedFlowTriggerVisual.background,
                  borderRadius: '50%',
                  padding: 0,
                  width: { xs: 60, md: 80 },
                  height: { xs: 60, md: 80 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isSelectedFlowMain ? 'grab' : 'pointer',
                  position: 'relative',
                  boxShadow: selectedFlowTriggerVisual.shadow,
                  opacity: selectedFlowTriggerMode === 'disabled' ? 0.82 : isSelectedFlowMain ? 0.92 : 1,
                  filter: selectedFlowTriggerMode === 'disabled' ? 'saturate(0.45) brightness(0.95)' : 'none',
                  '&:hover': isSelectedFlowMain ? {} : {
                    transform: 'translateY(-2px) scale(1.02)',
                    boxShadow: selectedFlowTriggerVisual.hoverShadow,
                  }
                }}
              >
                <Box sx={{
                  width: { xs: 38, md: 52 },
                  height: { xs: 38, md: 52 },
                  borderRadius: '50%',
                  background: selectedFlowTriggerVisual.iconBackground,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.55), 0 2px 8px rgba(0,0,0,0.08)'
                }}>
                  <TriggerModeIcon sx={{ fontSize: { xs: 24, md: 34 }, color: selectedFlowTriggerVisual.iconColor }} />
                </Box>
              </Box>
            </AppTooltip>
            <Box sx={{
              mt: 1.5,
              textAlign: 'center',
              minHeight: 32,
              maxHeight: 44,
              maxWidth: { xs: 100, md: 120 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden'
            }}>
              <Typography sx={{
                fontSize: { xs: 10, md: 12 },
                fontWeight: 500,
                textAlign: 'center',
                color: selectedFlowTriggerVisual.labelColor,
                width: '100%',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.2,
                padding: '4px 6px',
                borderRadius: '6px',
                background: selectedFlowTriggerVisual.labelBackground,
                border: `1px solid ${selectedFlowTriggerVisual.labelBorder}`
              }}>
                {selectedFlowTriggerModeLabel}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }

    if (node.kind === 'add') {
      return (
        <Box
          key={node.id}
          data-workflow-node="true"
          onPointerDown={(event) => handleCanvasNodePointerDown(node.id, event)}
          onPointerUp={(event) => handleCanvasNodePointerUp(node.id, event, handleOpenMenu)}
          sx={nodeCommonSx}
        >
          <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
            <AppTooltip
              title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {t('workflow.add_new_action', 'Add Action')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                    {t('workflow.add_new_action_tooltip', 'Click to add a new workflow action')}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <Box
                onClick={(event) => {
                  if (ignoredNodeClickRef.current === node.id) {
                    ignoredNodeClickRef.current = null;
                    return;
                  }
                  event.stopPropagation();
                  handleOpenMenu(event);
                }}
                aria-controls={menuOpen ? 'add-new-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
                sx={{
                  backgroundColor: '#ffffff',
                  color: '#5757b0',
                  border: '3px dashed rgba(87, 87, 176, 0.4)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  borderRadius: '50%',
                  padding: 0,
                  width: { xs: 60, md: 80 },
                  height: { xs: 60, md: 80 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    transform: 'translateY(-3px) scale(1.02)',
                    boxShadow: '0 8px 24px rgba(87, 87, 176, 0.25), 0 4px 8px rgba(87, 87, 176, 0.15)',
                    backgroundColor: '#f0f0ff',
                    border: '3px dashed rgba(87, 87, 176, 0.6)',
                    background: 'linear-gradient(135deg, #f0f0ff 0%, #e8eaf6 100%)',
                  }
                }}
              >
                <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 'bold', color: '#5757b0' }}>
                  +
                </Typography>
              </Box>
            </AppTooltip>
            <Box sx={{ mt: 1.5, textAlign: 'center', minHeight: 32, maxHeight: 44, maxWidth: { xs: 100, md: 120 }, overflow: 'hidden' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#5757b0', lineHeight: 1.2 }}>
                {t('workflow.add_new_action', 'Add Action')}
              </Typography>
            </Box>
            <AddActionMenu
              open={menuOpen}
              onClose={handleCloseMenu}
              onMenuItemSelect={handleMenuItemSelect}
              onPluginImported={handlePluginImported}
              pluginActions={pluginActions}
              caseid={caseid}
            />
          </Box>
        </Box>
      );
    }

    const setting = node.setting;
    const index = selectedFlowSettings.findIndex((item) => String(item.id) === String(setting.id));
    const group = normalizeActionExecutionGroup(setting.execution_group, setting.execution_mode);
    const isInParallelGroup = Boolean(group && normalizeActionExecutionMode(setting.execution_mode) === 'parallel');
    const canDelete = setting.type === 'generatewidget'
      ? selectedFlowSettings.filter((item) => item.type === 'generatewidget').length > 1
      : true;

    return (
      <Box
        key={node.id}
        data-workflow-node="true"
        onPointerDown={(event) => handleCanvasNodePointerDown(node.id, event)}
        onPointerUp={(event) => handleCanvasNodePointerUp(node.id, event, () => {
          setSelectedSettingId(setting.id);
          setSelectedSettingType(setting.type);
          setIsSettingDialogOpen(true);
        })}
        sx={nodeCommonSx}
      >
        <WorkflowActionNode
          setting={setting}
          index={index}
          totalSettings={selectedFlowSettings.length}
          isEnabled={setting.isEnabled}
          isSelected={selectedSettingId === setting.id}
          isSearchActive={Boolean(normalizedActionSearchQuery)}
          isSearchMatch={actionSearchMatchIds.has(String(setting.id))}
          status={isReplayingAllWorkflows ? getItemStatus(setting.id) : 'idle'}
          togglingScope={togglingScope === setting.id}
          onToggleEnable={() => handleToggleEnable(setting.id, setting.enable)}
          onToggleScope={() => handleToggleScope(setting.id, setting.scope || 'event')}
          onClick={() => {
            if (ignoredNodeClickRef.current === node.id) {
              ignoredNodeClickRef.current = null;
              return;
            }
            setSelectedSettingId(setting.id);
            setSelectedSettingType(setting.type);
            setIsSettingDialogOpen(true);
          }}
          onDelete={() => handleDeleteConfirm(setting.id)}
          onMove={(direction) => handleMoveSetting(setting.id, direction)}
          onMoveToFlow={handleMoveActionToFlow}
          onUngroup={handleUngroupAction}
          onToggleGroupMode={handleToggleExecutionGroupMode}
          onNameSave={handleNameSave}
          onCopyAction={handleCopyAction}
          canDelete={canDelete}
          workflowFlows={sortedFlows}
          currentFlowId={resolveSettingFlowId(setting)}
          isInParallelGroup={isInParallelGroup}
          groupMode={normalizeActionExecutionMode(setting.execution_mode)}
          styles={styles}
        />
      </Box>
    );
  };

  const renderCanvasJoinControls = () => selectedFlowExecutionStages.map((stage, stageIndex) => {
    if (stageIndex === 0) {
      return null;
    }

    const firstStageAction = stage.actions[0];
    if (!firstStageAction) {
      return null;
    }

    const firstStageActionIndex = selectedFlowSettings.findIndex((item) => String(item.id) === String(firstStageAction.id));
    const previousAction = firstStageActionIndex > 0
      ? selectedFlowSettings[firstStageActionIndex - 1]
      : null;
    const firstStageGroup = normalizeActionExecutionGroup(firstStageAction.execution_group, firstStageAction.execution_mode);
    const previousActionGroup = previousAction
      ? normalizeActionExecutionGroup(previousAction.execution_group, previousAction.execution_mode)
      : null;
    const canJoinStageWithPrevious = Boolean(
      firstStageAction
      && previousAction
      && (!firstStageGroup || previousActionGroup !== firstStageGroup)
    );
    if (!previousAction) {
      return null;
    }

    const sourcePosition = getCanvasNodePosition(`action:${previousAction.id}`);
    const targetPosition = getCanvasNodePosition(`action:${firstStageAction.id}`);
    const controlCenterX = (
      sourcePosition.x
      + targetPosition.x
      + WORKFLOW_CANVAS_NODE_WIDTH
    ) / 2;
    const controlCenterY = (
      sourcePosition.y
      + targetPosition.y
      + (WORKFLOW_CANVAS_NODE_CENTER_Y * 2)
    ) / 2;

    return (
      <AppTooltip
        key={`join-stage-${stageIndex}-${firstStageAction.id}`}
        title={t('workflow.group_parallel_with_previous', 'Run with previous action in parallel')}
        placement="top"
        arrow
      >
        <Box
          component="span"
          sx={{
            position: 'absolute',
            left: controlCenterX - 18,
            top: controlCenterY - 18,
            display: 'inline-flex',
            zIndex: 3
          }}
        >
          <IconButton
            size="small"
            disabled={!canJoinStageWithPrevious}
            aria-label={t('workflow.group_parallel_with_previous', 'Run with previous action in parallel')}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              if (canJoinStageWithPrevious) {
                handleGroupWithPrevious(firstStageAction.id);
              }
            }}
            sx={{
              width: 36,
              height: 36,
              border: '2px solid',
              backgroundColor: '#fff',
              borderColor: canJoinStageWithPrevious ? 'rgba(87, 87, 176, 0.45)' : 'rgba(0,0,0,0.12)',
              color: canJoinStageWithPrevious ? '#5757b0' : 'rgba(0,0,0,0.34)',
              boxShadow: canJoinStageWithPrevious ? '0 4px 12px rgba(87, 87, 176, 0.2)' : 'none',
              '&:hover': {
                backgroundColor: '#f4f3ff',
                borderColor: '#5757b0'
              }
            }}
          >
            <CallSplitIcon sx={{ fontSize: 19 }} />
          </IconButton>
        </Box>
      </AppTooltip>
    );
  });

  const renderWorkflowCanvas = () => (
    <Box
      ref={canvasContainerRef}
      sx={{
        position: 'relative',
        height: { xs: 420, md: 560 },
        border: '1px solid rgba(87, 87, 176, 0.18)',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: '#fbfbff',
        backgroundImage: 'radial-gradient(rgba(87,87,176,0.18) 1px, transparent 1px)',
        backgroundSize: '22px 22px',
        cursor: canvasInteractionRef.current?.type === 'pan' ? 'grabbing' : 'grab',
        mb: 3
      }}
      onPointerDown={handleCanvasPointerDown}
      onWheel={handleCanvasWheel}
    >
      <Box
        onPointerDown={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          right: 10,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 0.75,
          zIndex: 5
        }}
      >
        {(selectedFlowSettings.length > 0 || actionSearchQuery) && (
          <TextField
            size="small"
            value={actionSearchQuery}
            onChange={(event) => setActionSearchQuery(event.target.value)}
            placeholder={t('workflow.search_actions', 'Search actions')}
            aria-label={t('workflow.search_actions', 'Search actions')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: actionSearchQuery ? (
                <InputAdornment position="end" sx={{ ml: 0.25 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {actionSearchMatchCount}/{selectedFlowSettings.length}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label={t('template.clear_search', 'Clear Search')}
                    onClick={() => setActionSearchQuery('')}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{
              width: 'clamp(120px, 32vw, 260px)',
              '& .MuiOutlinedInput-root': {
                height: 34,
                borderRadius: '8px',
                backgroundColor: '#fff'
              }
            }}
          />
        )}
        <Button size="small" variant="outlined" onClick={() => handleZoomCanvas(-1)} sx={{ minWidth: 34, borderRadius: '8px' }}>-</Button>
        <Button size="small" variant="outlined" onClick={() => setCanvasResetConfirmOpen(true)} sx={{ borderRadius: '8px', textTransform: 'none' }}>
          {t('workflow.canvas_reset', 'Reset')}
        </Button>
        <Button size="small" variant="outlined" onClick={() => handleZoomCanvas(1)} sx={{ minWidth: 34, borderRadius: '8px' }}>+</Button>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: canvasBounds.width,
          height: canvasBounds.height,
          transform: `translate(${canvasViewport.x}px, ${canvasViewport.y}px) scale(${canvasViewport.scale})`,
          transformOrigin: '0 0'
        }}
      >
        <Box component="svg" sx={{ position: 'absolute', inset: 0, width: canvasBounds.width, height: canvasBounds.height, overflow: 'visible', pointerEvents: 'none' }}>
          <defs>
            <marker
              id="workflow-canvas-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(87, 87, 176, 0.72)" />
            </marker>
          </defs>
          {canvasBaseLayout.edges.map((edge, index) => {
            const source = getCanvasNodePosition(edge.sourceId);
            const target = getCanvasNodePosition(edge.targetId);
            const path = getWorkflowCanvasEdgePath(source, target);
            return (
              <path
                key={`${edge.sourceId}-${edge.targetId}-${index}`}
                d={path}
                fill="none"
                stroke="rgba(87, 87, 176, 0.42)"
                strokeWidth="2.5"
                strokeLinecap="round"
                markerEnd="url(#workflow-canvas-arrow)"
              />
            );
          })}
        </Box>
        {renderCanvasJoinControls()}
        {canvasBaseLayout.nodes.map(renderWorkflowCanvasNode)}
      </Box>
    </Box>
  );

  if (isLoading || isWorkflowFlowsLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', minHeight: '300px', p: 4 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">{t('workflow.loading', 'Loading workflows...')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ m: 1 }}>        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AppTooltip title={t('workflow.tooltip', 'Create automated workflows. Chain multiple actions together to build powerful automation sequences.')}>
            <IconButton size="small" sx={{ mr: 1 }}><InfoIcon fontSize="inherit" /></IconButton>
          </AppTooltip>
          <Typography variant="body2" color="text.secondary">
            {t('workflow.description', 'These actions will be executed automatically after every event is processed.')}
          </Typography>
        </Box>

        {/* Play Button Icon - Top Right */}
        {settings.length > 0 && (
          <AppTooltip
            title={
              isTimeeventCountLoading
                ? t('workflow.replay_count_loading', 'Checking project size...')
                : isFlowRunCountUnavailable
                  ? t('workflow.replay_count_unavailable', 'Unable to verify project size. Manual run is disabled.')
                : isFlowRunLimitExceeded
                  ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('workflow.replay_limit_exceeded_title', 'Manual run unavailable')}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        {t(
                          'workflow.replay_limit_exceeded_message',
                          'This project has {{count}} time events. Manual execution is limited to {{limit}}.',
                          { count: timeeventCount, limit: MAX_MANUAL_FLOW_RUN_TIMEEVENTS }
                        )}
                      </Typography>
                    </Box>
                  )
                  : (
                    <div>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('workflow.replay_all_tooltip_en', 'Replay All Workflows')}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {t('workflow.replay_all_tooltip_fr', 'Rejouer tous les flux de travail')}
                      </Typography>
                    </div>
                  )
            }
            arrow
            placement="left"
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
            <IconButton
              onClick={handleReplayConfirmOpen}
              disabled={isFlowRunDisabled}
              aria-label={t('workflow.replay_all_tooltip_en', 'Replay All Workflows')}
              sx={{
                backgroundColor: isFlowRunDisabled ? '#e0e0e0' : '#4caf50',
                color: 'white',
                width: { xs: 36, md: 48 },
                height: { xs: 36, md: 48 },
                borderRadius: '12px',
                boxShadow: isFlowRunDisabled
                  ? '0 2px 8px rgba(0,0,0,0.1)'
                  : '0 4px 16px rgba(76, 175, 80, 0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                border: '2px solid transparent',
                position: 'relative',
                overflow: 'hidden',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                  borderRadius: '12px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                },

                '&:hover': {
                  backgroundColor: isFlowRunDisabled ? '#e0e0e0' : '#45a049',
                  transform: isFlowRunDisabled ? 'none' : 'translateY(-2px) scale(1.05)',
                  boxShadow: isFlowRunDisabled
                    ? '0 2px 8px rgba(0,0,0,0.1)'
                    : '0 8px 24px rgba(76, 175, 80, 0.4)',

                  '&::before': {
                    opacity: isFlowRunDisabled ? 0 : 1,
                  }
                },

                '&:active': {
                  transform: isFlowRunDisabled ? 'none' : 'translateY(0px) scale(0.95)',
                },

                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {(isReplayingAllWorkflows || isTimeeventCountLoading) ? (
                <CircularProgress
                  size={20}
                  sx={{
                    color: '#9e9e9e',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    },
                    width: { xs: 20, md: 24 },
                    height: { xs: 20, md: 24 }
                  }}
                />
              ) : (
                <PlayArrowIcon
                  sx={{
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
                  }}
                />
              )}
            </IconButton>
            </Box>
          </AppTooltip>
        )}
      </Box>

        <WorkflowFlowHeader
          sortedFlows={sortedFlows}
          fallbackFlowId={fallbackFlowId}
          onSelectFlow={setSelectedFlowId}
          onCreateFlow={handleCreateFlow}
          selectedFlow={selectedFlow}
          onRenameFlow={handleRenameSelectedFlow}
          onDeleteFlow={handleDeleteSelectedFlow}
          canDeleteSelectedFlow={sortedFlows.length > 1 && !isSelectedFlowMain}
          t={t}
        />

        {renderWorkflowCanvas()}
        {false && (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', alignItems: 'flex-start', maxWidth: '900px' }}>
              {showFlowTriggerNode && (
                <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
                  <AppTooltip
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {t('workflow.automation_trigger_node_label', 'Flow Trigger')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                          {isSelectedFlowMain
                            ? t('workflow.automation_trigger_on_event', 'New or updated event')
                            : selectedFlowTriggerModeLabel || t('workflow.automation_trigger_click_config', 'Click to configure automation')}
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      onClick={handleTriggerNodeClick}
                      sx={{
                        color: selectedFlowTriggerVisual.iconColor,
                        border: `3px solid ${selectedFlowTriggerVisual.borderColor}`,
                        background: selectedFlowTriggerVisual.background,
                        borderRadius: '50%',
                        padding: '0px',
                        width: { xs: '60px', md: '80px' },
                        height: { xs: '60px', md: '80px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: isSelectedFlowMain ? 'default' : 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        boxShadow: selectedFlowTriggerVisual.shadow,
                        opacity: selectedFlowTriggerMode === 'disabled' ? 0.82 : isSelectedFlowMain ? 0.92 : 1,
                        filter: selectedFlowTriggerMode === 'disabled' ? 'saturate(0.45) brightness(0.95)' : 'none',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.04) 100%)',
                          pointerEvents: 'none'
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: '-10px',
                          left: '50%',
                          transform: 'translateX(-50%) rotate(45deg)',
                          width: '10px',
                          height: '10px',
                          border: '1px solid #dddddd',
                          borderWidth: '0 1px 1px 0',
                          backgroundColor: '#ffffff',
                          display: 'block'
                        },
                        '&:hover': isSelectedFlowMain ? {} : {
                          transform: 'translateY(-3px) scale(1.02)',
                          boxShadow: selectedFlowTriggerVisual.hoverShadow,
                        }
                      }}
                    >
                      <Box sx={{
                        width: { xs: 38, md: 52 },
                        height: { xs: 38, md: 52 },
                        borderRadius: '50%',
                        background: selectedFlowTriggerVisual.iconBackground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.55), 0 2px 8px rgba(0,0,0,0.08)',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <TriggerModeIcon sx={{ fontSize: { xs: 24, md: 34 }, color: selectedFlowTriggerVisual.iconColor }} />
                      </Box>
                    </Box>
                  </AppTooltip>
                  <Box sx={{
                    mt: 1.5,
                    textAlign: 'center',
                    minHeight: '32px',
                    maxHeight: '44px',
                    maxWidth: { xs: '100px', md: '120px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <Typography sx={{
                      fontSize: { xs: '10px', md: '12px' },
                      fontWeight: 500,
                      textAlign: 'center',
                      color: selectedFlowTriggerVisual.labelColor,
                      width: '100%',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.2',
                      padding: '4px 6px',
                      borderRadius: '6px',
                      background: selectedFlowTriggerVisual.labelBackground,
                      border: `1px solid ${selectedFlowTriggerVisual.labelBorder}`
                    }}>
                      {selectedFlowTriggerModeLabel}
                    </Typography>
                  </Box>
                </Box>
              )}

              {showFlowTriggerNode && selectedFlowSettings.length > 0 && (
                <WorkflowConnector />
              )}

              {selectedFlowExecutionStages.map((stage, stageIndex) => {
                const isParallelStage = stage.execution_mode === 'parallel' && stage.actions.length > 1;
                const firstStageAction = stage.actions[0];
                const firstStageActionIndex = firstStageAction
                  ? selectedFlowSettings.findIndex((item) => String(item.id) === String(firstStageAction.id))
                  : -1;
                const previousAction = firstStageActionIndex > 0
                  ? selectedFlowSettings[firstStageActionIndex - 1]
                  : null;
                const firstStageGroup = firstStageAction
                  ? normalizeActionExecutionGroup(firstStageAction.execution_group, firstStageAction.execution_mode)
                  : null;
                const previousActionGroup = previousAction
                  ? normalizeActionExecutionGroup(previousAction.execution_group, previousAction.execution_mode)
                  : null;
                const canJoinStageWithPrevious = Boolean(
                  stageIndex > 0
                  && firstStageAction
                  && previousAction
                  && (!firstStageGroup || previousActionGroup !== firstStageGroup)
                );

                const renderNode = (setting) => {
                  const index = selectedFlowSettings.findIndex((item) => String(item.id) === String(setting.id));
                  const isEnabled = setting.isEnabled;
                  const status = getItemStatus(setting.id);
                  const group = normalizeActionExecutionGroup(setting.execution_group, setting.execution_mode);
                  const isInParallelGroup = Boolean(group && normalizeActionExecutionMode(setting.execution_mode) === 'parallel');
                  const canDelete = setting.type === 'generatewidget'
                    ? selectedFlowSettings.filter((item) => item.type === 'generatewidget').length > 1
                    : true;
                  const previousSetting = index > 0 ? selectedFlowSettings[index - 1] : null;
                  const previousGroup = previousSetting
                    ? normalizeActionExecutionGroup(previousSetting.execution_group, previousSetting.execution_mode)
                    : null;
                  return (
                    <WorkflowActionNode
                      key={setting.id}
                      setting={setting}
                      index={index}
                      totalSettings={selectedFlowSettings.length}
                      isEnabled={isEnabled}
                      isSelected={selectedSettingId === setting.id}
                      isSearchActive={Boolean(normalizedActionSearchQuery)}
                      isSearchMatch={actionSearchMatchIds.has(String(setting.id))}
                      status={isReplayingAllWorkflows ? status : 'idle'}
                      togglingScope={togglingScope === setting.id}
                      onToggleEnable={() => handleToggleEnable(setting.id, setting.enable)}
                      onToggleScope={() => handleToggleScope(setting.id, setting.scope || 'event')}
                      onClick={() => {
                        setSelectedSettingId(setting.id);
                        setSelectedSettingType(setting.type);
                        setIsSettingDialogOpen(true);
                      }}
                      onDelete={() => handleDeleteConfirm(setting.id)}
                      onMove={(direction) => handleMoveSetting(setting.id, direction)}
                      onMoveToFlow={handleMoveActionToFlow}
                      onUngroup={handleUngroupAction}
                      onToggleGroupMode={handleToggleExecutionGroupMode}
                      onNameSave={handleNameSave}
                      onCopyAction={handleCopyAction}
                      canDelete={canDelete}
                      workflowFlows={sortedFlows}
                      currentFlowId={resolveSettingFlowId(setting)}
                      isInParallelGroup={isInParallelGroup}
                      groupMode={normalizeActionExecutionMode(setting.execution_mode)}
                      styles={styles}
                    />
                  );
                };

                return (
                  <React.Fragment key={stage.execution_group || stage.actions[0]?.id || stageIndex}>
                    {stageIndex > 0 && (
                      <WorkflowJoinConnector
                        canJoinPrevious={canJoinStageWithPrevious}
                        onJoinPrevious={() => handleGroupWithPrevious(firstStageAction.id)}
                        label={t('workflow.parallel_join_button', 'Parallel')}
                        tooltip={t('workflow.group_parallel_with_previous', 'Run with previous action in parallel')}
                      />
                    )}

                    {isParallelStage ? (
                      <Box sx={{
                        border: '1px solid rgba(87, 87, 176, 0.25)',
                        borderRadius: '8px',
                        px: 1.5,
                        py: 1,
                        background: 'rgba(87, 87, 176, 0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <Typography sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#5757b0',
                          textTransform: 'uppercase',
                        }}>
                          {t('workflow.parallel_group', 'Parallel group')}
                        </Typography>
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          gap: { xs: 1, md: 2 },
                        }}>
                          {stage.actions.map(renderNode)}
                        </Box>
                      </Box>
                    ) : (
                      stage.actions.map(renderNode)
                    )}
                  </React.Fragment>
                );
              })}

              {/* Arrow before Add Action Button */}
              {selectedFlowSettings.length > 0 && (
                <WorkflowConnector />
              )}

              <Box sx={WORKFLOW_NODE_CONTAINER_SX}>
                <AppTooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {t('workflow.add_new_action', 'Add Action')}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                        Click to add a new workflow action
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    onClick={handleOpenMenu}
                    aria-controls={menuOpen ? "add-new-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={menuOpen ? "true" : undefined}
                    sx={{
                      backgroundColor: '#ffffff',
                      color: '#5757b0',
                      border: '3px dashed rgba(87, 87, 176, 0.4)',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      borderRadius: '50%',
                      padding: '0px',
                      width: { xs: '60px', md: '80px' },
                      height: { xs: '60px', md: '80px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      boxShadow: '0 2px 8px rgba(87, 87, 176, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)',
                      opacity: 1,
                      transform: 'scale(1)',

                      '&:hover': {
                        transform: 'translateY(-3px) scale(1.02)',
                        boxShadow: '0 8px 24px rgba(87, 87, 176, 0.25), 0 4px 8px rgba(87, 87, 176, 0.15)',
                        backgroundColor: '#f0f0ff',
                        border: '3px dashed rgba(87, 87, 176, 0.6)',
                        background: 'linear-gradient(135deg, #f0f0ff 0%, #e8eaf6 100%)',
                      },

                      '&:active': {
                        transform: 'translateY(0px) scale(0.98)',
                      }
                    }}
                  >
                    <Typography sx={{
                      fontSize: { xs: '24px', md: '32px' },
                      fontWeight: 'bold',
                      color: '#5757b0',
                      textShadow: '0 1px 2px rgba(87, 87, 176, 0.2)',
                      transition: 'all 0.3s ease'
                    }}>
                      +
                    </Typography>
                  </Box>
                </AppTooltip>

                {/* Text Label Below Circular Button */}
                <Box sx={{
                  mt: 1.5,
                  textAlign: 'center',
                  minHeight: '32px',
                  maxHeight: '44px', // Maximum height for 2 lines of text
                  maxWidth: { xs: '100px', md: '120px' }, // Maximum width to prevent text from becoming too wide
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden' // Hide overflow if text is still too long
                }}>
                  <Typography sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    textAlign: 'center',
                    color: '#5757b0',
                    width: '100%',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2, // Allow maximum 2 lines of text
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.2', // Tight line height for compact display
                    padding: '4px 6px',
                    borderRadius: '6px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                    '&:hover': {
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      transform: 'scale(1.02)',
                    }
                  }}>
                    {t('workflow.add_new_action', 'Add Action')}
                  </Typography>
                </Box>
                <AddActionMenu
                  open={menuOpen}
                  onClose={handleCloseMenu}
                  onMenuItemSelect={handleMenuItemSelect}
                  onPluginImported={handlePluginImported}
                  pluginActions={pluginActions}
                  caseid={caseid}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {/* Removed old conditional rendering of settings components here */}

      {/* Dialog for Editing Settings - Now uses the new component */}
      <SettingsDialog
        open={isSettingDialogOpen && selectedSettingId !== null}
        onClose={handleSettingDialogClose}
        setting={currentSettingForDialog} // Pass the whole setting object
        settingType={selectedSettingType}
        tags={dialogTags}
        caseid={caseid}
        onSaveStatusChange={handleSettingSaveStatusChange}
        onScopeChange={handleScopeChange}
        onTargetExecutionChange={handleActionTargetExecutionChange}
        onActionCommand={sendTemplateAwareCommand}
        isScopeChanging={togglingScope === selectedSettingId}
        pluginActions={pluginActions}
        workflowFlows={sortedFlows}
        onCreateWorkflowFlow={handleCreateWorkflowFlowFromAction}
        t={t} // Pass translation function
      />

      <WorkflowFlowAutomationDialog
        open={flowAutomationDialogOpen}
        mode={flowAutomationDialogMode}
        flowName={flowAutomationDialogName}
        triggerMode={flowAutomationDialogTriggerMode}
        triggerFilter={flowAutomationDialogTriggerFilter}
        frequencyValue={flowAutomationDialogFrequencyValue}
        frequencyUnit={flowAutomationDialogFrequencyUnit}
        scheduleDayOfWeek={flowAutomationDialogScheduleDayOfWeek}
        scheduleDayOfMonth={flowAutomationDialogScheduleDayOfMonth}
        scheduleMonth={flowAutomationDialogScheduleMonth}
        scheduleHour={flowAutomationDialogScheduleHour}
        scheduleMinute={flowAutomationDialogScheduleMinute}
        scheduleTimezone={flowAutomationDialogScheduleTimezone}
        calendarWeekdayOptions={calendarWeekdayOptions}
        calendarDayOfMonthOptions={calendarDayOfMonthOptions}
        calendarMonthOptions={calendarMonthOptions}
        calendarHourOptions={calendarHourOptions}
        calendarMinuteOptions={calendarMinuteOptions}
        error={flowAutomationDialogError}
        isSubmitting={flowAutomationDialogSubmitting}
        onClose={handleCloseFlowAutomationDialog}
        onSubmit={handleSubmitFlowAutomationDialog}
        onFlowNameChange={(value) => {
          setFlowAutomationDialogName(value);
          setFlowAutomationDialogError('');
        }}
        onTriggerModeChange={(value) => {
          setFlowAutomationDialogTriggerMode(normalizeFlowTriggerMode(value));
          setFlowAutomationDialogError('');
        }}
        onTriggerFilterChange={(value) => {
          setFlowAutomationDialogTriggerFilter(normalizeFlowTriggerFilter(value));
          setFlowAutomationDialogError('');
        }}
        onFrequencyValueChange={(value) => {
          setFlowAutomationDialogFrequencyValue(value);
          setFlowAutomationDialogError('');
        }}
        onFrequencyUnitChange={(value) => {
          setFlowAutomationDialogFrequencyUnit(value);
          setFlowAutomationDialogError('');
        }}
        onScheduleDayOfWeekChange={(value) => {
          setFlowAutomationDialogScheduleDayOfWeek(normalizeFlowScheduleDayOfWeek(value));
          setFlowAutomationDialogError('');
        }}
        onScheduleDayOfMonthChange={(value) => {
          setFlowAutomationDialogScheduleDayOfMonth(normalizeFlowScheduleDayOfMonth(value));
          setFlowAutomationDialogError('');
        }}
        onScheduleMonthChange={(value) => {
          setFlowAutomationDialogScheduleMonth(normalizeFlowScheduleMonth(value));
          setFlowAutomationDialogError('');
        }}
        onScheduleHourChange={(value) => {
          setFlowAutomationDialogScheduleHour(normalizeFlowScheduleHour(value));
          setFlowAutomationDialogError('');
        }}
        onScheduleMinuteChange={(value) => {
          setFlowAutomationDialogScheduleMinute(normalizeFlowScheduleMinute(value));
          setFlowAutomationDialogError('');
        }}
        t={t}
      />

      <WorkflowFlowManageDialogs
        renameOpen={flowRenameDialogOpen}
        renameValue={flowRenameDialogValue}
        renameError={flowRenameDialogError}
        deleteOpen={flowDeleteDialogOpen}
        selectedFlowName={selectedFlow?.name || ''}
        onRenameClose={handleCloseFlowRenameDialog}
        onRenameValueChange={(value) => {
          setFlowRenameDialogValue(value);
          setFlowRenameDialogError('');
        }}
        onRenameSubmit={handleSubmitFlowRenameDialog}
        onDeleteClose={handleCloseFlowDeleteDialog}
        onDeleteSubmit={handleSubmitFlowDeleteDialog}
        t={t}
      />

      <Dialog
        open={canvasResetConfirmOpen}
        onClose={() => setCanvasResetConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('workflow.canvas_reset_confirm_title', 'Reset node positions?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t(
              'workflow.canvas_reset_confirm_message',
              'All nodes in this flow will return to their default positions. This cannot be undone.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCanvasResetConfirmOpen(false)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmCanvasReset}>
            {t('workflow.canvas_reset_confirm_action', 'Reset positions')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scopeChangeDialogOpen && Boolean(pendingScopeChange)}
        onClose={handleCancelScopeChange}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {pendingScopeChange?.title || t('workflow.scope_change_confirm_title', 'Confirm scope change')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {pendingScopeChange?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelScopeChange}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="warning" onClick={handleConfirmScopeChange}>
            {pendingScopeChange?.confirmLabel || t('workflow.scope_change_confirm_action', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={templateDecisionDialogOpen}
        onClose={handleCloseTemplateDecisionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('template.change_decision_title', 'This project is linked to a template')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t(
              'template.change_decision_message',
              `You changed ${templateDecisionData?.changeLabel || 'this action'}. Choose whether to keep the change only in this project or propagate it to all linked projects.`
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {(templateDecisionData?.templateTitle || t('dashboard.template_fallback', 'Template'))}
            {templateDecisionData?.templateId ? ` (#${templateDecisionData.templateId})` : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelTemplateDecision}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => handleTemplateDecision('detach')}
          >
            {t('template.detach_project', 'Disconnect Project')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleTemplateDecision('propagate')}
          >
            {t('template.update_template', 'Update Template')}
          </Button>
        </DialogActions>
      </Dialog>

      <WorkflowConfirmDialogs
        replayOpen={replayConfirmOpen}
        onReplayClose={handleReplayConfirmClose}
        onReplayConfirm={handleReplayAllWorkflows}
        deleteOpen={deleteConfirmOpen}
        onDeleteClose={handleDeleteCancel}
        onDeleteConfirm={handleDeleteExecute}
        t={t}
      />

      {/* Workflow Error Notification */}
      <Snackbar
        open={!!workflowError}
        autoHideDuration={8000}
        onClose={() => setWorkflowError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setWorkflowError(null)}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
            maxWidth: '600px',
            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
          }}
        >
          <Box>
            <Box sx={{ fontWeight: 'bold', marginBottom: 1, fontSize: '1.1em' }}>
              Workflow Step Failed
            </Box>
            {workflowError && (
              <>
                <Box sx={{ fontSize: '0.95em', marginBottom: 0.5 }}>
                  <strong>Step:</strong> {workflowError.stepName} ({workflowError.stepIndex}/{workflowError.totalSteps})
                </Box>
                <Box sx={{ fontSize: '0.95em', marginBottom: 0.5 }}>
                  <strong>Error:</strong> {workflowError.errorMessage}
                </Box>
                <Box sx={{ fontSize: '0.85em', opacity: 0.9, marginTop: 1, fontStyle: 'italic' }}>
                  Remaining workflows were not executed
                </Box>
              </>
            )}
          </Box>
        </Alert>
      </Snackbar>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ActionsDataflowList;

